import React, { useState, useEffect, useMemo, useRef } from "react";
import { AppSettings, RateCard, AssetRateCard, FolderDefinition, UserRole, UserStatus } from "../types";
import { DEFAULT_RATES, DEFAULT_ASSET_RATES, DEFAULT_SCRIPT_PROMPTS } from "../constants";
import { ArrowLeft, Plus, Trash2, Check, CheckCircle2, Settings as SettingsIcon, ChevronRight, ChevronDown, Folder, Palette, Database, Users, Shield, Globe, RefreshCw, Loader2, User, CircleDashed, Link2, Boxes, Search } from "lucide-react";
import Papa from "papaparse";
import { toast } from "sonner";
import { RateCardForm } from "./RateCardForm";
import { useAuth } from "../contexts/AuthContext";
import { cn } from "../utils";
import { UserAccessEditor } from "./UserAccessEditor";
import { ACCESS_ROLE_OPTIONS, ACCESS_STATUS_OPTIONS, PERMISSION_GROUPS, ROLE_DEFAULT_PERMISSIONS, getRoleLabel, getStatusLabel, getStatusToneClasses } from "../lib/accessControl";
import { buildPersonIntegrationSummary, summarizePeopleIntegrationCounts } from "../lib/personIntegrations";
import { MarkdownField } from "./MarkdownField";
import { DEFAULT_SETUP } from "../App";

const FOLDER_COLORS = [
  { name: 'Chocolate', value: '#ac725e' },
  { name: 'Terra Cotta', value: '#d06b64' },
  { name: 'Cardinal', value: '#f83a22' },
  { name: 'Tomato', value: '#fa573c' },
  { name: 'Pumpkin', value: '#ff7537' },
  { name: 'Tangerine', value: '#ffad46' },
  { name: 'Spearmint', value: '#42d692' },
  { name: 'Vernal Green', value: '#16a765' },
  { name: 'Basil', value: '#7bd148' },
  { name: 'Pistachio', value: '#b3dc6c' },
  { name: 'Celandine', value: '#fbe983' },
  { name: 'Saffron', value: '#fad165' },
  { name: 'Sage', value: '#92e1c0' },
  { name: 'Sky Blue', value: '#9fe1e7' },
  { name: 'Rainy Sky', value: '#9fc6e7' },
  { name: 'Sea Blue', value: '#4986e7' },
  { name: 'Lavender', value: '#9a9cff' },
  { name: 'Wisteria', value: '#b99aff' },
  { name: 'Graphite', value: '#c2c2c2' },
  { name: 'Blueberry', value: '#cabdbf' },
  { name: 'Bubblegum', value: '#cca6ac' },
  { name: 'Grape', value: '#f691b2' },
  { name: 'Amethyst', value: '#cd74e6' },
  { name: 'Toy Eggplant', value: '#a47ae2' },
];

const INTEGRATION_ICON_STYLE: Record<string, { bg: string; icon: string; ring: string; accent: string }> = {
  google: {
    bg: "bg-white",
    icon: "https://www.gstatic.com/images/branding/product/1x/drive_48dp.png",
    ring: "ring-[#dfe7fb]",
    accent: "text-[#4285f4]",
  },
  fpt: {
    bg: "bg-slate-900",
    icon: "/fpt-icon.svg",
    ring: "ring-slate-200",
    accent: "text-slate-900",
  },
  okta: {
    bg: "bg-white",
    icon: "/okta-icon.svg",
    ring: "ring-[#d9e6ff]",
    accent: "text-[#0b5cff]",
  },
  slack: {
    bg: "bg-white",
    icon: "",
    ring: "ring-[#f0e4ef]",
    accent: "text-[#7c3aed]",
  },
};

type StaffImportFieldKey =
  | "name"
  | "firstName"
  | "lastName"
  | "email"
  | "photoUrl"
  | "title"
  | "department"
  | "phone"
  | "personalEmail"
  | "personalPhone"
  | "address"
  | "addressLine2"
  | "city"
  | "stateProvince"
  | "postalCode"
  | "country"
  | "contractType"
  | "contractStartDate"
  | "contractEndDate"
  | "salary"
  | "dailyRate"
  | "annualLeaveAllowance"
  | "offerLetterDate"
  | "companyName";

type StaffImportMapping = Partial<Record<StaffImportFieldKey, string>>;
type StaffImportMappingConfidence = "exact" | "loose" | "saved";

type StaffImportPreviewRow = {
  rowNumber: number;
  action: "create" | "update" | "skip" | "review";
  matchReason: "email" | "name" | "phone" | "postal_last_name" | "email_local_part" | "fuzzy_name" | "csv_duplicate" | "csv_contract_variation" | "csv_contract_overlap" | "none";
  name: string;
  email: string;
  photoUrl?: string;
  contractStartDate?: string;
  contractEndDate?: string;
  conflictingRowNumber?: number;
  conflictingContractStartDate?: string;
  conflictingContractEndDate?: string;
  warnings: string[];
  candidateMatches?: Array<{
    personId: string;
    name: string;
    email: string;
    department: string;
    title: string;
    matchReason: StaffImportPreviewRow["matchReason"];
    confidenceLabel: "strong" | "possible";
    matchSummary: string;
  }>;
  selectedPersonId?: string;
};

type StaffImportPreviewSummary = {
  totalRows: number;
  actionableRows: number;
  createdCount: number;
  updatedCount: number;
  skippedCount: number;
  reviewCount: number;
  markedInactiveCount: number;
  rows: StaffImportPreviewRow[];
};

type StaffImportReviewDecision =
  | { decision: "link_existing"; personId: string }
  | { decision: "create_new" }
  | { decision: "skip" };

function formatStaffImportContractRange(startDate?: string, endDate?: string) {
  if (!startDate && !endDate) return "Dates not provided";

  const formatDate = (value?: string) => {
    if (!value) return "Open";
    const normalized = new Date(`${value}T00:00:00`);
    if (Number.isNaN(normalized.getTime())) {
      return value;
    }
    return normalized.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  return `${formatDate(startDate)} - ${formatDate(endDate)}`;
}

const STAFF_IMPORT_FIELD_OPTIONS: Array<{ key: StaffImportFieldKey; label: string; description: string }> = [
  { key: "name", label: "Full Name", description: "Preferred primary name field for matching and creation." },
  { key: "firstName", label: "First Name", description: "Used with Last Name if Full Name is absent." },
  { key: "lastName", label: "Last Name", description: "Used with First Name if Full Name is absent." },
  { key: "email", label: "Work Email", description: "Primary email for exact matching." },
  { key: "photoUrl", label: "Photo URL", description: "Remote image URL to import as the canonical profile photo." },
  { key: "title", label: "Job Title", description: "Current title on the person record." },
  { key: "department", label: "Department", description: "Primary department on the person record." },
  { key: "phone", label: "Work Phone", description: "Main phone stored on the person record." },
  { key: "personalEmail", label: "Personal Email", description: "Stored in the HR profile." },
  { key: "personalPhone", label: "Personal Phone", description: "Stored in the HR profile." },
  { key: "address", label: "Address", description: "Stored in the HR profile." },
  { key: "addressLine2", label: "Address Line 2", description: "Stored in the HR profile." },
  { key: "city", label: "City", description: "Stored in the HR profile." },
  { key: "stateProvince", label: "State / Province", description: "Stored in the HR profile." },
  { key: "postalCode", label: "Postal Code", description: "Stored in the HR profile." },
  { key: "country", label: "Country", description: "Stored in the HR profile." },
  { key: "contractType", label: "Contract Type", description: "Current contract type on the HR profile." },
  { key: "contractStartDate", label: "Contract Start", description: "Current contract start date." },
  { key: "contractEndDate", label: "Contract End", description: "Current contract end date." },
  { key: "salary", label: "Annual Salary", description: "Current annual salary in the HR profile." },
  { key: "dailyRate", label: "Daily Rate", description: "Current daily rate in the HR profile." },
  { key: "annualLeaveAllowance", label: "Annual Leave", description: "Current leave allowance in days." },
  { key: "offerLetterDate", label: "Offer Letter Date", description: "Offer letter date for the employment record." },
  { key: "companyName", label: "Company Name", description: "Company name for the employment record." },
];

const STAFF_IMPORT_HEADER_ALIASES: Record<StaffImportFieldKey, string[]> = {
  name: ["name", "full name", "employee name", "staff name"],
  firstName: ["first name", "forename", "given name"],
  lastName: ["last name", "surname", "family name"],
  email: ["email", "work email", "company email", "email address"],
  photoUrl: ["photo url", "photo", "image url", "avatar url", "profile image", "profile photo", "headshot url"],
  title: ["title", "job title", "role"],
  department: ["department", "team"],
  phone: ["phone", "work phone", "telephone", "mobile", "work mobile"],
  personalEmail: ["personal email", "private email"],
  personalPhone: ["personal phone", "private phone", "home phone"],
  address: ["address", "home address", "address line 1"],
  addressLine2: ["address line 2", "address 2"],
  city: ["city", "town"],
  stateProvince: ["state", "state province", "county", "province"],
  postalCode: ["postcode", "postal code", "zip", "zip code"],
  country: ["country"],
  contractType: ["contract type", "employment type"],
  contractStartDate: ["start date", "contract start", "employment start date"],
  contractEndDate: ["end date", "contract end", "employment end date"],
  salary: ["salary", "annual salary"],
  dailyRate: ["daily rate", "day rate"],
  annualLeaveAllowance: ["annual leave", "leave allowance", "holiday allowance"],
  offerLetterDate: ["offer letter date", "offer date"],
  companyName: ["company name", "employer", "company"],
};

const STAFF_IMPORT_MAPPING_STORAGE_KEY = "staff_import_mapping_preset_v1";

function normalizeStaffImportHeader(value: string) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");
}

function buildSuggestedStaffImportMappings(headers: string[]): {
  mapping: StaffImportMapping;
  confidence: Partial<Record<StaffImportFieldKey, StaffImportMappingConfidence>>;
} {
  const normalizedHeaders = headers.map((header) => ({
    raw: header,
    normalized: normalizeStaffImportHeader(header),
  }));

  const nextMapping: StaffImportMapping = {};
  const nextConfidence: Partial<Record<StaffImportFieldKey, StaffImportMappingConfidence>> = {};
  STAFF_IMPORT_FIELD_OPTIONS.forEach((field) => {
    const aliases = STAFF_IMPORT_HEADER_ALIASES[field.key];
    const exactMatch = normalizedHeaders.find((header) =>
      aliases.some((alias) => header.normalized === alias)
    );
    if (exactMatch) {
      nextMapping[field.key] = exactMatch.raw;
      nextConfidence[field.key] = "exact";
      return;
    }

    const looseMatch = normalizedHeaders.find((header) =>
      aliases.some((alias) =>
        alias.length > 4 && (header.normalized.includes(alias) || alias.includes(header.normalized))
      )
    );
    if (looseMatch) {
      nextMapping[field.key] = looseMatch.raw;
      nextConfidence[field.key] = "loose";
    }
  });
  return { mapping: nextMapping, confidence: nextConfidence };
}

function readSavedStaffImportMapping(): StaffImportMapping {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STAFF_IMPORT_MAPPING_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    return parsed as StaffImportMapping;
  } catch (error) {
    console.warn("Failed to read saved staff import mapping", error);
    return {};
  }
}

function saveStaffImportMappingPreset(mapping: StaffImportMapping) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STAFF_IMPORT_MAPPING_STORAGE_KEY, JSON.stringify(mapping));
  } catch (error) {
    console.warn("Failed to save staff import mapping", error);
  }
}

function buildStaffImportMappingsForHeaders(headers: string[]): {
  mapping: StaffImportMapping;
  confidence: Partial<Record<StaffImportFieldKey, StaffImportMappingConfidence>>;
} {
  const suggested = buildSuggestedStaffImportMappings(headers);
  const saved = readSavedStaffImportMapping();
  const headerSet = new Set(headers);
  const nextMapping: StaffImportMapping = { ...suggested.mapping };
  const nextConfidence: Partial<Record<StaffImportFieldKey, StaffImportMappingConfidence>> = { ...suggested.confidence };

  STAFF_IMPORT_FIELD_OPTIONS.forEach((field) => {
    const savedColumn = saved[field.key];
    if (savedColumn && headerSet.has(savedColumn)) {
      nextMapping[field.key] = savedColumn;
      nextConfidence[field.key] = "saved";
    }
  });

  return { mapping: nextMapping, confidence: nextConfidence };
}

function getStaffImportMappingWarnings(mapping: StaffImportMapping) {
  const warnings: string[] = [];
  const fullNameColumn = mapping.name || "";
  const firstNameColumn = mapping.firstName || "";
  const lastNameColumn = mapping.lastName || "";

  if (fullNameColumn && firstNameColumn && fullNameColumn === firstNameColumn) {
    warnings.push("Full Name is mapped to the same CSV column as First Name. Leave Full Name unmapped when First Name and Surname are already mapped.");
  }

  if (fullNameColumn && lastNameColumn && fullNameColumn === lastNameColumn) {
    warnings.push("Full Name is mapped to the same CSV column as Last Name. Leave Full Name unmapped when First Name and Surname are already mapped.");
  }

  const normalizedFullNameColumn = normalizeStaffImportHeader(fullNameColumn);
  if (fullNameColumn && (normalizedFullNameColumn === "first name" || normalizedFullNameColumn === "surname" || normalizedFullNameColumn === "last name")) {
    warnings.push("Full Name appears to be mapped to a partial name column. Use a true full-name column, or leave it unmapped and rely on First Name + Last Name.");
  }

  return warnings;
}

function getMissingRequiredStaffImportMappings(mapping: StaffImportMapping) {
  const missing: string[] = [];
  const hasFullName = Boolean(mapping.name);
  const hasSplitName = Boolean(mapping.firstName && mapping.lastName);

  if (!hasFullName && !hasSplitName) {
    missing.push("map Full Name, or map both First Name and Last Name");
  }

  return missing;
}

function IntegrationMark({
  integrationKey,
  label,
  size = "sm",
  bare = false,
}: {
  integrationKey: string;
  label: string;
  size?: "sm" | "md" | "lg";
  bare?: boolean;
}) {
  const style = INTEGRATION_ICON_STYLE[integrationKey] || {
    bg: "bg-white",
    icon: "",
    ring: "ring-slate-200",
    accent: "text-slate-400",
  };

  const sizeClasses =
    size === "lg"
      ? "h-12 w-12 rounded-2xl"
      : size === "md"
        ? "h-6 w-6 rounded-xl"
        : "h-4 w-4";
  const iconClasses =
    size === "lg"
      ? "h-7 w-7"
      : size === "md"
        ? "h-3.5 w-3.5"
        : "h-2.5 w-2.5";
  const textClasses = size === "lg" ? "text-xs" : "text-[8px]";

  return (
    <span
      className={cn(
        "flex items-center justify-center",
        bare ? "shadow-none ring-0 bg-transparent rounded-none" : cn("ring-1 shadow-sm", sizeClasses, style.bg, style.ring),
        bare && size === "sm" ? "h-4 w-4" : null,
        bare && size === "md" ? "h-5 w-5" : null,
        bare && size === "lg" ? "h-8 w-8" : null
      )}
    >
      {style.icon ? (
        <img src={style.icon} alt={label} className={cn("object-contain", iconClasses)} referrerPolicy="no-referrer" />
      ) : integrationKey === "okta" ? (
        <span className={cn("font-bold uppercase", bare ? style.accent : "text-white", textClasses)}>O</span>
      ) : integrationKey === "slack" ? (
        <Boxes className={cn(iconClasses, style.accent)} />
      ) : (
        <CircleDashed className={cn(iconClasses, style.accent)} />
      )}
    </span>
  );
}

interface FolderItemProps {
  folder: FolderDefinition;
  onUpdate: (folder: FolderDefinition) => void;
  onDelete: () => void;
  level: number;
  driveColors: { name: string, value: string }[];
}

function FolderItem({ folder, onUpdate, onDelete, level, driveColors }: FolderItemProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showColorPicker, setShowColorPicker] = useState(false);

  const handleAddChild = () => {
    const newChild: FolderDefinition = {
      id: Math.random().toString(36).substr(2, 9),
      name: "New Folder",
      color: folder.color,
      children: []
    };
    onUpdate({
      ...folder,
      children: [...(folder.children || []), newChild]
    });
    setIsExpanded(true);
  };

  const handleUpdateChild = (index: number, updatedChild: FolderDefinition) => {
    const newChildren = [...(folder.children || [])];
    newChildren[index] = updatedChild;
    onUpdate({ ...folder, children: newChildren });
  };

  const handleDeleteChild = (index: number) => {
    const newChildren = (folder.children || []).filter((_, i) => i !== index);
    onUpdate({ ...folder, children: newChildren });
  };

  return (
    <div className="space-y-0.5">
      <div 
        className="flex items-center gap-1.5 group rounded-md px-1 py-0.5 hover:bg-slate-50 transition-colors"
      >
        <button 
          onClick={() => setIsExpanded(!isExpanded)}
          className={cn(
            "p-0.5 hover:bg-slate-200 rounded transition-colors",
            (!folder.children || folder.children.length === 0) && "opacity-0 cursor-default"
          )}
        >
          {isExpanded ? <ChevronDown className="w-3.5 h-3.5 text-slate-500" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-500" />}
        </button>

        <div 
          className="w-2.5 h-2.5 rounded-full shrink-0 shadow-sm border border-white/20"
          style={{ backgroundColor: folder.color || '#64748b' }}
        />

        <input
          type="text"
          value={folder.name || ""}
          onChange={(e) => onUpdate({ ...folder, name: e.target.value })}
          className="flex-1 bg-transparent border-none focus:ring-0 text-xs font-medium text-slate-700 p-0"
          placeholder="Folder name..."
        />

        <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <label className="flex items-center gap-1.5 cursor-pointer" title="Limit Access (Managers Only)">
            <input
              type="checkbox"
              checked={folder.limitAccess || false}
              onChange={(e) => onUpdate({ ...folder, limitAccess: e.target.checked })}
              className="w-3.5 h-3.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="hidden text-[10px] font-medium text-slate-500 uppercase tracking-wider xl:inline">Limit</span>
          </label>
          <div className="w-px h-3 bg-slate-200" />
          <div className="relative">
            <button
              onClick={() => setShowColorPicker(!showColorPicker)}
              className="p-1 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
              title="Change color"
            >
              <Palette className="w-3.5 h-3.5" />
            </button>
            {showColorPicker && (
              <div className="absolute right-0 top-full mt-1 p-2 bg-white rounded-xl shadow-xl border border-slate-200 z-50 grid grid-cols-6 gap-1 w-48">
                {driveColors.map((c) => (
                  <button
                    key={c.value}
                    onClick={() => {
                      onUpdate({ ...folder, color: c.value });
                      setShowColorPicker(false);
                    }}
                    className="w-6 h-6 rounded-full border border-slate-200 hover:scale-110 transition-transform"
                    style={{ backgroundColor: c.value }}
                    title={c.name}
                  />
                ))}
              </div>
            )}
          </div>
          <button
            onClick={handleAddChild}
            className="p-1 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
            title="Add subfolder"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={onDelete}
            className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
            title="Remove folder"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {isExpanded && folder.children && folder.children.length > 0 && (
        <div className="ml-[9px] pl-3 border-l border-slate-200 space-y-0.5">
          {folder.children.map((child, idx) => (
            <FolderItem
              key={child.id || `child-${idx}`}
              folder={child}
              onUpdate={(updated) => handleUpdateChild(idx, updated)}
              onDelete={() => handleDeleteChild(idx)}
              level={level + 1}
              driveColors={driveColors}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function IntegrationBadge({
  integrationKey,
  label,
  state,
}: {
  integrationKey: string;
  label: string;
  state: string;
}) {
  const linked = state === "linked";
  const showDisconnectedWarning = !linked && (integrationKey === "google" || integrationKey === "fpt");
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-medium",
        linked
          ? "bg-emerald-50 text-emerald-700"
          : showDisconnectedWarning
            ? "bg-rose-50 text-rose-700"
            : "bg-slate-100 text-slate-500"
      )}
    >
      <IntegrationMark integrationKey={integrationKey} label={label} bare />
      {label}
    </span>
  );
}

function IntegrationStatusBadge({
  linked,
}: {
  linked: boolean;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider",
        linked
          ? "border-emerald-100 bg-emerald-50 text-emerald-700"
          : "border-slate-200 bg-slate-50 text-slate-600"
      )}
    >
      {linked ? <Link2 className="h-3 w-3" /> : <CircleDashed className="h-3 w-3" />}
      {linked ? "Linked" : "Unlinked"}
    </span>
  );
}

interface Props {
  onBack: () => void;
  projectId?: string | null;
  driveConnected: boolean;
  setDriveConnected: (connected: boolean) => void;
  settings: AppSettings | null;
  onSettingsChange: (settings: AppSettings | ((prev: AppSettings) => AppSettings)) => void;
  isSaving?: boolean;
  onOpenPeoplePerson?: (personId: string) => void;
}

type IntegrationsSummary = {
  drive: {
    connected: boolean;
    sharedDriveCount: number | null;
    linkedProjectCount: number;
    trackedDocumentCount: number;
  };
  fpt: {
    linkedProjectCount: number;
  };
};

type FptProjectImportPreviewRow = {
  fptProjectId: string;
  fptProjectName: string;
  fptProjectStatus?: string;
  fptShowCode?: string;
  startDate?: string;
  endDate?: string;
  decision: "create" | "skip" | "review";
  reason?: string;
  matchedLocalProjectId?: string;
  matchedLocalProjectName?: string;
  matchedLocalProjectLinked?: boolean;
  note?: string;
};

type FptProjectImportPreview = {
  summary: {
    total: number;
    createCount: number;
    skipCount: number;
    reviewCount: number;
    existingLinkedCount: number;
  };
  rows: FptProjectImportPreviewRow[];
};

type FptProjectImportResult = {
  summary: {
    total: number;
    createdCount: number;
    skippedCount: number;
    reviewCount: number;
  };
  created: Array<{
    projectId: string;
    projectName: string;
    fptProjectId: string;
  }>;
  skipped: Array<{
    fptProjectId: string;
    fptProjectName: string;
    reason: string;
  }>;
};

type FptProjectStatusSyncResult = {
  summary: {
    totalLinkedProjects: number;
    updatedCount: number;
    unchangedCount: number;
    skippedCount: number;
  };
};

type ManualFptPersonCandidate = {
  id: string;
  name: string;
  email: string;
  status: string;
  statusRaw?: unknown;
};

type FptProjectImportFilter = "all" | "create" | "review" | "linked";
type DateDisplayFormat = "dd-mm-yyyy" | "dd/mm/yyyy" | "yyyy-mm-dd";

type UserPreferencesPayload = {
  settings?: {
    staffAccess?: {
      grouping?: "none" | "department" | "role" | "status";
      focusFilter?: "all" | "google_unlinked" | "fpt_unlinked";
      statusFilters?: string[];
    };
    display?: {
      dateFormat?: DateDisplayFormat;
    };
  };
};

export function Settings({ onBack, projectId, driveConnected, setDriveConnected, settings: externalSettings, onSettingsChange, isSaving, onOpenPeoplePerson }: Props) {
  const { isAdmin, user, hasPermission } = useAuth();
  
  // Use external settings if provided, otherwise fallback to defaults
  const settings = externalSettings || {
    defaultRates: DEFAULT_RATES,
    defaultAssetRates: DEFAULT_ASSET_RATES,
    defaultAssumptions: DEFAULT_SETUP.assumptions,
    scriptPrompts: DEFAULT_SCRIPT_PROMPTS,
    inviteOnlyEnabled: false,
    domainRestrictionEnabled: false,
    allowedDomains: ["vinefx.com"],
    folderStructure: [
      { id: '1', name: "Bids", color: '#4986e7', children: [] },
      { id: '2', name: "Reference", color: '#9fe1e7', children: [] },
      { id: '3', name: "Assets", color: '#16a765', children: [] },
      { id: '4', name: "Legal", color: '#fad165', children: [] },
      { id: '5', name: "Production", color: '#f83a22', children: [] },
    ],
  };

  const setSettings = (update: AppSettings | ((prev: AppSettings) => AppSettings)) => {
    if (typeof update === 'function') {
      onSettingsChange(prev => update(prev || settings));
    } else {
      onSettingsChange(update);
    }
  };

  const [loading, setLoading] = useState(!externalSettings);
  const [driveColors, setDriveColors] = useState<{ name: string, value: string }[]>(FOLDER_COLORS);
  const canManageIntegrations = isAdmin || hasPermission("integrations.manage");
  const canManageDrive = isAdmin || hasPermission("drive.manage");
  const canManageSettings = isAdmin || hasPermission("settings.manage");
  const canManageUsers = isAdmin || hasPermission("users.manage");
  const canManageTrash = isAdmin || hasPermission("trash.manage");

  useEffect(() => {
    const fetchDriveColors = async (retries = 3) => {
      try {
        const res = await fetch("/api/drive/colors", {
          headers: { "x-user-uid": user?.uid || "" }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.colors && Array.isArray(data.colors)) {
            const mappedColors = data.colors.map((hex: string, index: number) => ({
              name: `Drive Color ${index + 1}`,
              value: hex
            }));
            setDriveColors(mappedColors);
          }
        }
      } catch (error) {
        if (retries > 0) {
          setTimeout(() => fetchDriveColors(retries - 1), 2000);
        } else {
          console.error("Failed to fetch drive colors:", error);
        }
      }
    };
    fetchDriveColors();
  }, []);

  const [activeTab, setActiveTab] = useState<"rates" | "prompts" | "integrations" | "drive" | "trash" | "storage" | "domain" | "permissions">("rates");
  const [domainUsers, setDomainUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [editingAccessUser, setEditingAccessUser] = useState<any | null>(null);
  const [linkingFptUser, setLinkingFptUser] = useState<any | null>(null);
  const [manualFptSearchQuery, setManualFptSearchQuery] = useState("");
  const [manualFptCandidates, setManualFptCandidates] = useState<ManualFptPersonCandidate[]>([]);
  const [manualFptSearching, setManualFptSearching] = useState(false);
  const [manualFptLinking, setManualFptLinking] = useState(false);
  const [checkingFpt, setCheckingFpt] = useState(false);
  const [matchingFptStaff, setMatchingFptStaff] = useState(false);
  const [previewingFptProjectImport, setPreviewingFptProjectImport] = useState(false);
  const [importingFptProjects, setImportingFptProjects] = useState(false);
  const [syncingFptProjectStatuses, setSyncingFptProjectStatuses] = useState(false);
  const [fptProjectImportFilter, setFptProjectImportFilter] = useState<FptProjectImportFilter>("all");
  const [selectedFptProjectImportIds, setSelectedFptProjectImportIds] = useState<string[]>([]);
  const [fptMatchScope, setFptMatchScope] = useState<"active_only" | "include_inactive" | "include_all">("active_only");
  const [fptStatus, setFptStatus] = useState<{ connected: boolean; message: string } | null>(null);
  const [integrationSummary, setIntegrationSummary] = useState<IntegrationsSummary | null>(null);
  const [loadingIntegrationSummary, setLoadingIntegrationSummary] = useState(false);
  const [fptProjectImportPreview, setFptProjectImportPreview] = useState<FptProjectImportPreview | null>(null);
  const [selectedStaffIds, setSelectedStaffIds] = useState<string[]>([]);
  const [bulkRole, setBulkRole] = useState<UserRole | "">("");
  const [bulkStatus, setBulkStatus] = useState<string>("");
  const [applyingBulkAccess, setApplyingBulkAccess] = useState(false);
  const [staffGrouping, setStaffGrouping] = useState<"none" | "department" | "role" | "status">("none");
  const [staffFocusFilter, setStaffFocusFilter] = useState<"all" | "google_unlinked" | "fpt_unlinked">("all");
  const [staffStatusFilters, setStaffStatusFilters] = useState<string[]>(() => ACCESS_STATUS_OPTIONS.map((option) => option.value));
  const [dateDisplayFormat, setDateDisplayFormat] = useState<DateDisplayFormat>("dd-mm-yyyy");
  const [staffPreferencesReady, setStaffPreferencesReady] = useState(false);
  const [showStaffStatusFilterMenu, setShowStaffStatusFilterMenu] = useState(false);
  const [showStaffImportModal, setShowStaffImportModal] = useState(false);
  const [staffImportHeaders, setStaffImportHeaders] = useState<string[]>([]);
  const [staffImportRows, setStaffImportRows] = useState<Record<string, string>[]>([]);
  const [staffImportFileName, setStaffImportFileName] = useState("");
  const [staffImportMapping, setStaffImportMapping] = useState<StaffImportMapping>({});
  const [staffImportMappingConfidence, setStaffImportMappingConfidence] = useState<
    Partial<Record<StaffImportFieldKey, StaffImportMappingConfidence>>
  >({});
  const [staffImportPreview, setStaffImportPreview] = useState<StaffImportPreviewSummary | null>(null);
  const [staffImportLoading, setStaffImportLoading] = useState(false);
  const [staffImportApplying, setStaffImportApplying] = useState(false);
  const [staffImportSetMissingInactive, setStaffImportSetMissingInactive] = useState(false);
  const [staffImportReviewDecisions, setStaffImportReviewDecisions] = useState<Record<string, StaffImportReviewDecision | undefined>>({});
  const staffImportInputRef = useRef<HTMLInputElement | null>(null);
  const staffImportReviewListRef = useRef<HTMLDivElement | null>(null);
  const staffAccessSectionRef = useRef<HTMLDivElement | null>(null);
  const staffStatusFilterRef = useRef<HTMLDivElement | null>(null);
  const lastSavedStaffPreferencesRef = useRef("");
  const lastSavedDisplayPreferencesRef = useRef("");
  const staffIntegrationCounts = summarizePeopleIntegrationCounts(domainUsers);
  const filteredDomainUsers = useMemo(() => {
    return domainUsers.filter((entry) => {
      if (staffStatusFilters.length > 0 && !staffStatusFilters.includes(entry.status || "active")) {
        return false;
      }

      if (staffFocusFilter === "all") {
        return true;
      }

      const integrationStatus = entry.integrationStatus || buildPersonIntegrationSummary(entry);
      if (staffFocusFilter === "google_unlinked") {
        return integrationStatus.google?.state !== "linked";
      }
      if (staffFocusFilter === "fpt_unlinked") {
        return integrationStatus.fpt?.state !== "linked";
      }
      return true;
    });
  }, [domainUsers, staffFocusFilter, staffStatusFilters]);
  const visibleSelectedStaffIds = useMemo(
    () => filteredDomainUsers.filter((entry) => selectedStaffIds.includes(entry.id)).map((entry) => entry.id),
    [filteredDomainUsers, selectedStaffIds]
  );
  const groupedStaffRows = useMemo(() => {
    const sortedUsers = [...filteredDomainUsers].sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" })
    );

    if (staffGrouping === "none") {
      return [{ key: "all", label: "", users: sortedUsers }];
    }

    const getGroupLabel = (entry: any) => {
      if (staffGrouping === "department") return entry.department || "No department";
      if (staffGrouping === "role") return getRoleLabel(entry.role || "standard");
      return getStatusLabel(entry.status || "active");
    };

    const groups = new Map<string, any[]>();
    sortedUsers.forEach((entry) => {
      const label = getGroupLabel(entry);
      const current = groups.get(label) || [];
      current.push(entry);
      groups.set(label, current);
    });

    return Array.from(groups.entries())
      .sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: "base" }))
      .map(([label, users]) => ({
        key: label,
        label,
        users,
      }));
  }, [filteredDomainUsers, staffGrouping]);

  const visibleStaffImportPreviewRows = useMemo(() => staffImportRows.slice(0, 8), [staffImportRows]);
  const staffImportMappingWarnings = useMemo(
    () => getStaffImportMappingWarnings(staffImportMapping),
    [staffImportMapping],
  );
  const missingRequiredStaffImportMappings = useMemo(
    () => getMissingRequiredStaffImportMappings(staffImportMapping),
    [staffImportMapping],
  );
  const unresolvedStaffImportReviews = useMemo(
    () =>
      (staffImportPreview?.rows || []).filter(
        (row) => row.action === "review" && !staffImportReviewDecisions[String(row.rowNumber)],
      ).length,
    [staffImportPreview, staffImportReviewDecisions],
  );
  const unresolvedStaffImportReviewRows = useMemo(
    () =>
      (staffImportPreview?.rows || []).filter(
        (row) => row.action === "review" && !staffImportReviewDecisions[String(row.rowNumber)],
      ),
    [staffImportPreview, staffImportReviewDecisions],
  );
  const visibleStaffImportReviewRows = useMemo(() => {
    const rows = staffImportPreview?.rows || [];
    const reviewRows = rows.filter((row) => row.action === "review");
    if (reviewRows.length > 0) {
      return [...reviewRows].sort((left, right) => {
        const leftName = String(left.name || "").trim().toLocaleLowerCase();
        const rightName = String(right.name || "").trim().toLocaleLowerCase();
        if (leftName !== rightName) {
          return leftName.localeCompare(rightName, undefined, { sensitivity: "base" });
        }
        return left.rowNumber - right.rowNumber;
      });
    }
    return rows.slice(0, 12);
  }, [staffImportPreview]);
  const firstUnresolvedStaffImportReviewRow = useMemo(
    () =>
      unresolvedStaffImportReviewRows[0] || null,
    [unresolvedStaffImportReviewRows],
  );
  const allSafeFptProjectImportIds = useMemo(
    () => (fptProjectImportPreview?.rows || []).filter((row) => row.decision === "create").map((row) => row.fptProjectId),
    [fptProjectImportPreview],
  );
  const filteredFptProjectImportRows = useMemo(() => {
    const rows = fptProjectImportPreview?.rows || [];
    if (fptProjectImportFilter === "all") return rows;
    if (fptProjectImportFilter === "create") return rows.filter((row) => row.decision === "create");
    if (fptProjectImportFilter === "review") return rows.filter((row) => row.decision === "review");
    return rows.filter((row) => row.decision === "skip");
  }, [fptProjectImportFilter, fptProjectImportPreview]);
  const staffImportDisabledReason = useMemo(() => {
    if (staffImportApplying) return "Import is currently running.";
    if (missingRequiredStaffImportMappings.length > 0) return `Before previewing, ${missingRequiredStaffImportMappings[0]}.`;
    if (staffImportMappingWarnings.length > 0) return staffImportMappingWarnings[0];
    if (!staffImportPreview) return "Run Preview Import first.";
    return "";
  }, [missingRequiredStaffImportMappings, staffImportApplying, staffImportMappingWarnings, staffImportPreview]);

  const scrollToFirstUnresolvedStaffImportReview = () => {
    if (!firstUnresolvedStaffImportReviewRow) return;
    const element = staffImportReviewListRef.current?.querySelector(
      `[data-staff-import-row="${firstUnresolvedStaffImportReviewRow.rowNumber}"]`,
    );
    element?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  useEffect(() => {
    if (canManageSettings) return;
    if (canManageUsers) {
      setActiveTab("domain");
      return;
    }
    if (canManageIntegrations) {
      setActiveTab("integrations");
      return;
    }
    if (canManageDrive) {
      setActiveTab("drive");
      return;
    }
    if (canManageTrash) {
      setActiveTab("trash");
    }
  }, [canManageDrive, canManageIntegrations, canManageSettings, canManageTrash, canManageUsers]);

  const checkFptStatus = async () => {
    setCheckingFpt(true);
    try {
      const res = await fetch("/api/fpt/test-connection");
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setFptStatus({
          connected: true,
          message: data.result?.serverInfo
            ? `Connected to ${data.result.serverInfo}.`
            : "Connected to Autodesk Flow Production Tracking.",
        });
      } else {
        setFptStatus({
          connected: false,
          message: data.error || "Connection test failed.",
        });
      }
    } catch (error: any) {
      setFptStatus({
        connected: false,
        message: error?.message || "Connection test failed.",
      });
    } finally {
      setCheckingFpt(false);
    }
  };

  const loadIntegrationSummary = async () => {
    if (!user) return;
    setLoadingIntegrationSummary(true);
    try {
      const res = await fetch("/api/integrations/summary", {
        headers: {
          "x-user-uid": user.uid,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || "Failed to load integration summary");
      }
      setIntegrationSummary(data as IntegrationsSummary);
    } catch (error) {
      console.error("Failed to load integration summary", error);
    } finally {
      setLoadingIntegrationSummary(false);
    }
  };

  const matchFptStaff = async () => {
    if (!user) return;
    setMatchingFptStaff(true);
    try {
      const res = await fetch("/api/fpt/people/match", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-uid": user.uid,
        },
        body: JSON.stringify({
          matchScope: fptMatchScope,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || "Failed to match Flow Production Tracking staff");
      }

      const matchedCount = Number(data.summary?.matchedCount || 0);
      const ambiguousCount = Number(data.summary?.ambiguousCount || 0);
      const skippedNoCandidateCount = Number(data.summary?.skippedNoCandidateCount || 0);

      if (matchedCount > 0) {
        toast.success(
          `Matched ${matchedCount} Flow Production Tracking staff link${matchedCount === 1 ? "" : "s"}.`,
          {
            description:
              ambiguousCount > 0 || skippedNoCandidateCount > 0
                ? `${ambiguousCount} ambiguous · ${skippedNoCandidateCount} with no candidate`
                : undefined,
          },
        );
      } else {
        toast.message("No new Flow Production Tracking staff links were made.", {
          description:
            ambiguousCount > 0 || skippedNoCandidateCount > 0
              ? `${ambiguousCount} ambiguous · ${skippedNoCandidateCount} with no candidate`
              : "Everyone already linked or no safe matches were found.",
        });
      }

      await fetchDomainUsers();
    } catch (error: any) {
      toast.error(error?.message || "Failed to match Flow Production Tracking staff");
    } finally {
      setMatchingFptStaff(false);
    }
  };

  const previewFptProjectImport = async () => {
    if (!user) return;
    setPreviewingFptProjectImport(true);
    try {
      const res = await fetch("/api/fpt/projects/import/preview", {
        headers: {
          "x-user-uid": user.uid,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || "Failed to preview Flow Production Tracking project import");
      }

      const preview: FptProjectImportPreview = {
        summary: data.summary,
        rows: Array.isArray(data.rows) ? data.rows : [],
      };
      setFptProjectImportPreview(preview);
      setSelectedFptProjectImportIds(
        preview.rows.filter((row) => row.decision === "create").map((row) => row.fptProjectId),
      );
      toast.success("Flow Production Tracking project import preview ready.", {
        description: `${preview.summary.createCount} safe to create · ${preview.summary.reviewCount} need review`,
      });
    } catch (error: any) {
      toast.error(error?.message || "Failed to preview Flow Production Tracking project import");
    } finally {
      setPreviewingFptProjectImport(false);
    }
  };

  const importFptProjects = async () => {
    if (!user) return;
    setImportingFptProjects(true);
    try {
      const res = await fetch("/api/fpt/projects/import", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-uid": user.uid,
        },
        body: JSON.stringify({
          projectIds: selectedFptProjectImportIds,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || "Failed to import Flow Production Tracking projects");
      }

      const result = data as FptProjectImportResult;
      toast.success("Flow Production Tracking projects imported.", {
        description: `${result.summary.createdCount} created · ${result.summary.reviewCount} still need review`,
      });
      await loadIntegrationSummary();
      await previewFptProjectImport();
    } catch (error: any) {
      toast.error(error?.message || "Failed to import Flow Production Tracking projects");
    } finally {
      setImportingFptProjects(false);
    }
  };

  const syncFptProjectStatuses = async () => {
    if (!user) return;
    setSyncingFptProjectStatuses(true);
    try {
      const res = await fetch("/api/fpt/projects/sync-statuses", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-uid": user.uid,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || "Failed to sync Flow Production Tracking project statuses");
      }

      const result = data as FptProjectStatusSyncResult;
      toast.success("Project statuses synced from Flow Production Tracking.", {
        description: `${result.summary.updatedCount} updated · ${result.summary.unchangedCount} unchanged · ${result.summary.skippedCount} skipped`,
      });
    } catch (error: any) {
      toast.error(error?.message || "Failed to sync Flow Production Tracking project statuses");
    } finally {
      setSyncingFptProjectStatuses(false);
    }
  };

  const fetchDomainUsers = async () => {
    if (!user) return;
    setLoadingUsers(true);
    try {
      const res = await fetch("/api/users", {
        headers: { "x-user-uid": user.uid }
      });
      if (res.ok) {
        setDomainUsers(await res.json());
      }
    } catch (error) {
      console.error("Failed to fetch domain users:", error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const searchFptPeople = async (query: string, matchScope = fptMatchScope) => {
    if (!user) return;
    setManualFptSearching(true);
    try {
      const params = new URLSearchParams({
        query,
        matchScope,
        limit: "20",
      });
      const res = await fetch(`/api/fpt/people/search?${params.toString()}`, {
        headers: { "x-user-uid": user.uid },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || "Failed to search Flow Production Tracking users");
      }
      setManualFptCandidates(Array.isArray(data.results) ? data.results : []);
    } catch (error: any) {
      toast.error(error?.message || "Failed to search Flow Production Tracking users");
      setManualFptCandidates([]);
    } finally {
      setManualFptSearching(false);
    }
  };

  const openManualFptLinker = (personRecord: any) => {
    const startingQuery = String(personRecord.email || personRecord.name || "").trim();
    setLinkingFptUser(personRecord);
    setManualFptSearchQuery(startingQuery);
    setManualFptCandidates([]);
    void searchFptPeople(startingQuery || String(personRecord.name || "").trim());
  };

  const submitManualFptLink = async (candidate: ManualFptPersonCandidate) => {
    if (!user || !linkingFptUser) return;
    setManualFptLinking(true);
    try {
      const res = await fetch(`/api/users/${linkingFptUser.id}/link-fpt`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "x-user-uid": user.uid,
        },
        body: JSON.stringify({
          fptPersonId: candidate.id,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || "Failed to link Flow Production Tracking user");
      }

      setDomainUsers((current) =>
        current.map((entry) => (entry.id === linkingFptUser.id ? { ...entry, ...data.user } : entry)),
      );
      toast.success(`Linked ${linkingFptUser.name || linkingFptUser.email} to ${candidate.name}.`);
      setLinkingFptUser(null);
      setManualFptCandidates([]);
      setManualFptSearchQuery("");
    } catch (error: any) {
      toast.error(error?.message || "Failed to link Flow Production Tracking user");
    } finally {
      setManualFptLinking(false);
    }
  };

  const resetStaffImportState = () => {
    setStaffImportHeaders([]);
    setStaffImportRows([]);
    setStaffImportFileName("");
    setStaffImportMapping({});
    setStaffImportMappingConfidence({});
    setStaffImportPreview(null);
    setStaffImportReviewDecisions({});
    setStaffImportLoading(false);
    setStaffImportApplying(false);
    setStaffImportSetMissingInactive(false);
    if (staffImportInputRef.current) {
      staffImportInputRef.current.value = "";
    }
  };

  const closeStaffImportModal = () => {
    setShowStaffImportModal(false);
    resetStaffImportState();
  };

  const handleOpenStaffImport = () => {
    setShowStaffImportModal(true);
  };

  const handleStaffImportFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        const headers = (result.meta.fields || []).filter(Boolean);
        const normalizedRows = (result.data || [])
          .map((row) =>
            Object.fromEntries(
              Object.entries(row || {}).map(([key, value]) => [String(key), String(value ?? "").trim()])
            )
          )
          .filter((row) => Object.values(row).some((value) => value));

        const initialMappings = buildStaffImportMappingsForHeaders(headers);
        setStaffImportFileName(file.name);
        setStaffImportHeaders(headers);
        setStaffImportRows(normalizedRows);
        setStaffImportMapping(initialMappings.mapping);
        setStaffImportMappingConfidence(initialMappings.confidence);
        setStaffImportPreview(null);
        setStaffImportReviewDecisions({});

        if (!headers.length || !normalizedRows.length) {
          toast.error("This CSV looks empty.");
          return;
        }

        toast.success(`Loaded ${normalizedRows.length} staff row${normalizedRows.length === 1 ? "" : "s"} from ${file.name}.`);
      },
      error: (error) => {
        console.error("Failed to parse staff CSV", error);
        toast.error("Failed to read that CSV file.");
      },
    });
  };

  const updateStaffImportMapping = (field: StaffImportFieldKey, column: string) => {
    setStaffImportMapping((current) => ({
      ...current,
      [field]: column || undefined,
    }));
    setStaffImportMappingConfidence((current) => {
      const next = { ...current };
      delete next[field];
      return next;
    });
    setStaffImportPreview(null);
    setStaffImportReviewDecisions({});
  };

  const updateStaffImportReviewDecision = (rowNumber: number, value: string) => {
    setStaffImportReviewDecisions((current) => {
      const next = { ...current };
      if (!value) {
        delete next[String(rowNumber)];
        return next;
      }
      if (value === "create_new") {
        next[String(rowNumber)] = { decision: "create_new" };
        return next;
      }
      if (value === "skip") {
        next[String(rowNumber)] = { decision: "skip" };
        return next;
      }
      if (value.startsWith("link:")) {
        next[String(rowNumber)] = {
          decision: "link_existing",
          personId: value.replace(/^link:/, ""),
        };
      }
      return next;
    });
  };

  const runStaffImportPreview = async (dryRun: boolean) => {
    if (!user) return;
    if (!staffImportRows.length) {
      toast.info("Choose a CSV file first.");
      return;
    }

    if (missingRequiredStaffImportMappings.length > 0) {
      toast.info(`Before previewing, ${missingRequiredStaffImportMappings[0]}.`);
      return;
    }

    if (staffImportMappingWarnings.length > 0) {
      toast.error(staffImportMappingWarnings[0]);
      return;
    }

    saveStaffImportMappingPreset(staffImportMapping);

    if (dryRun) {
      setStaffImportLoading(true);
    } else {
      setStaffImportApplying(true);
    }

    try {
      const res = await fetch("/api/people/import/internal-users", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-uid": user.uid,
          "x-user-email": user.email || "",
        },
        body: JSON.stringify({
          rows: staffImportRows,
          mapping: staffImportMapping,
          setMissingInactive: staffImportSetMissingInactive,
          dryRun,
          reviewDecisions: staffImportReviewDecisions,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (!dryRun && data.summary) {
          setStaffImportPreview(data.summary);
          toast.error(data.error || "Resolve all review rows before importing.");
          setTimeout(() => {
            scrollToFirstUnresolvedStaffImportReview();
          }, 50);
          return;
        }
        throw new Error(data.error || "Failed to process import.");
      }

      setStaffImportPreview(data.summary || null);

      if (dryRun) {
        toast.success("Import preview ready.");
      } else {
        toast.success("Internal staff import complete.");
        await fetchDomainUsers();
        closeStaffImportModal();
      }
    } catch (error: any) {
      toast.error(error?.message || "Failed to process import.");
    } finally {
      setStaffImportLoading(false);
      setStaffImportApplying(false);
    }
  };

  const toggleStaffSelection = (userId: string) => {
    setSelectedStaffIds((current) =>
      current.includes(userId)
        ? current.filter((id) => id !== userId)
        : [...current, userId]
    );
  };

  const toggleSelectAllStaff = () => {
    setSelectedStaffIds((current) =>
      visibleSelectedStaffIds.length === filteredDomainUsers.length
        ? current.filter((id) => !filteredDomainUsers.some((entry) => entry.id === id))
        : Array.from(new Set([...current, ...filteredDomainUsers.map((entry) => entry.id)]))
    );
  };

  const handleFocusStaffFilter = (nextFilter: "all" | "google_unlinked" | "fpt_unlinked") => {
    setStaffFocusFilter(nextFilter);
    window.requestAnimationFrame(() => {
      staffAccessSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  useEffect(() => {
    if (activeTab === "domain") {
      fetchDomainUsers();
    }
  }, [activeTab, user]);

  useEffect(() => {
    if (activeTab === "integrations" && canManageIntegrations) {
      void checkFptStatus();
      void loadIntegrationSummary();
    }
  }, [activeTab, canManageIntegrations, driveConnected, user]);

  useEffect(() => {
    if (!showStaffStatusFilterMenu) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (!staffStatusFilterRef.current?.contains(event.target as Node)) {
        setShowStaffStatusFilterMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showStaffStatusFilterMenu]);

  useEffect(() => {
    if (!user) {
      setStaffPreferencesReady(false);
      lastSavedStaffPreferencesRef.current = "";
      lastSavedDisplayPreferencesRef.current = "";
      return;
    }

    let cancelled = false;
    const validStatusValues = new Set(ACCESS_STATUS_OPTIONS.map((option) => option.value));
    const loadUserPreferences = async () => {
      setStaffPreferencesReady(false);
      try {
        const res = await fetch("/api/user-preferences", {
          headers: {
            "x-user-uid": user.uid,
            "x-user-email": user.email || "",
          },
        });
        if (!res.ok) {
          throw new Error("Failed to load user preferences");
        }
        const data = (await res.json()) as { preferences?: UserPreferencesPayload };
        if (cancelled) return;
        const staffPrefs = data.preferences?.settings?.staffAccess;
        if (staffPrefs?.grouping && ["none", "department", "role", "status"].includes(staffPrefs.grouping)) {
          setStaffGrouping(staffPrefs.grouping);
        }
        if (staffPrefs?.focusFilter && ["all", "google_unlinked", "fpt_unlinked"].includes(staffPrefs.focusFilter)) {
          setStaffFocusFilter(staffPrefs.focusFilter);
        }
        if (Array.isArray(staffPrefs?.statusFilters)) {
          const filtered = staffPrefs.statusFilters.filter((value): value is string => validStatusValues.has(value as any));
          setStaffStatusFilters(filtered);
        }
        const savedDateFormat = data.preferences?.settings?.display?.dateFormat;
        if (savedDateFormat && ["dd-mm-yyyy", "dd/mm/yyyy", "yyyy-mm-dd"].includes(savedDateFormat)) {
          setDateDisplayFormat(savedDateFormat as DateDisplayFormat);
        }
        lastSavedStaffPreferencesRef.current = JSON.stringify({
          grouping: staffPrefs?.grouping || "none",
          focusFilter: staffPrefs?.focusFilter || "all",
          statusFilters: Array.isArray(staffPrefs?.statusFilters)
            ? staffPrefs.statusFilters.filter((value): value is string => validStatusValues.has(value as any))
            : ACCESS_STATUS_OPTIONS.map((option) => option.value),
        });
        lastSavedDisplayPreferencesRef.current = JSON.stringify({
          dateFormat: savedDateFormat && ["dd-mm-yyyy", "dd/mm/yyyy", "yyyy-mm-dd"].includes(savedDateFormat)
            ? savedDateFormat
            : "dd-mm-yyyy",
        });
      } catch (error) {
        console.warn("Failed to load staff access preferences", error);
        lastSavedStaffPreferencesRef.current = JSON.stringify({
          grouping: "none",
          focusFilter: "all",
          statusFilters: ACCESS_STATUS_OPTIONS.map((option) => option.value),
        });
        lastSavedDisplayPreferencesRef.current = JSON.stringify({
          dateFormat: "dd-mm-yyyy",
        });
      } finally {
        if (!cancelled) {
          setStaffPreferencesReady(true);
        }
      }
    };

    void loadUserPreferences();

    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (!user || !staffPreferencesReady) return;

    const nextState = {
      grouping: staffGrouping,
      focusFilter: staffFocusFilter,
      statusFilters: staffStatusFilters,
    };
    const serialized = JSON.stringify(nextState);
    if (serialized === lastSavedStaffPreferencesRef.current) return;

    const timeout = window.setTimeout(async () => {
      try {
        const res = await fetch("/api/user-preferences", {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "x-user-uid": user.uid,
            "x-user-email": user.email || "",
          },
          body: JSON.stringify({
            preferences: {
              settings: {
                staffAccess: nextState,
              },
            },
          }),
        });
        if (!res.ok) {
          throw new Error("Failed to save user preferences");
        }
        lastSavedStaffPreferencesRef.current = serialized;
      } catch (error) {
        console.warn("Failed to save staff access preferences", error);
      }
    }, 250);

    return () => window.clearTimeout(timeout);
  }, [user, staffPreferencesReady, staffGrouping, staffFocusFilter, staffStatusFilters]);

  useEffect(() => {
    if (!user || !staffPreferencesReady) return;

    const nextState = { dateFormat: dateDisplayFormat };
    const serialized = JSON.stringify(nextState);
    if (serialized === lastSavedDisplayPreferencesRef.current) return;

    const timeout = window.setTimeout(async () => {
      try {
        const res = await fetch("/api/user-preferences", {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "x-user-uid": user.uid,
            "x-user-email": user.email || "",
          },
          body: JSON.stringify({
            preferences: {
              settings: {
                display: nextState,
              },
            },
          }),
        });
        if (!res.ok) {
          throw new Error("Failed to save display preferences");
        }
        lastSavedDisplayPreferencesRef.current = serialized;
      } catch (error) {
        console.warn("Failed to save display preferences", error);
      }
    }, 250);

    return () => window.clearTimeout(timeout);
  }, [user, staffPreferencesReady, dateDisplayFormat]);

  const handleUpdateUserAccess = async (
    userId: string,
    updates: { role: UserRole; status: string; customPermissions: string[] },
    options?: { skipRefresh?: boolean }
  ) => {
    if (!user) return;
    const res = await fetch(`/api/users/${userId}/access`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "x-user-uid": user.uid
      },
      body: JSON.stringify(updates)
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.error || "Failed to update user access");
    }

    if (data.user) {
      setDomainUsers((current) => current.map((entry) => entry.id === userId ? { ...entry, ...data.user } : entry));
      setEditingAccessUser((current: any) => current?.id === userId ? { ...current, ...data.user } : current);
    }

    if (!options?.skipRefresh) {
      await fetchDomainUsers();
    }
  };

  const toggleStaffStatusFilter = (statusValue: string) => {
    setStaffStatusFilters((current) => {
      if (current.includes(statusValue)) {
        return current.filter((value) => value !== statusValue);
      }
      return [...current, statusValue];
    });
  };

  const allStaffStatusesSelected = staffStatusFilters.length === ACCESS_STATUS_OPTIONS.length;
  const activeStaffStatusCount = staffStatusFilters.length;
  const staffStatusFilterLabel = allStaffStatusesSelected
    ? "Filter"
    : activeStaffStatusCount === 0
      ? "Filter: none"
      : activeStaffStatusCount === 1
        ? `Filter: ${getStatusLabel(staffStatusFilters[0] as UserStatus)}`
        : `Filter: ${activeStaffStatusCount} statuses`;

  const handleApplyBulkAccess = async () => {
    if (!selectedStaffIds.length || (!bulkRole && !bulkStatus)) {
      toast.info("Select staff and choose a role or status first.");
      return;
    }

    setApplyingBulkAccess(true);
    try {
      const selectedUsers = domainUsers.filter((entry) => selectedStaffIds.includes(entry.id));
      for (const selectedUser of selectedUsers) {
        await handleUpdateUserAccess(
          selectedUser.id,
          {
            role: (bulkRole || selectedUser.role || "standard") as UserRole,
            status: bulkStatus || selectedUser.status || "active",
            customPermissions: selectedUser.customPermissions || [],
          },
          { skipRefresh: true }
        );
      }

      await fetchDomainUsers();
      toast.success(`Updated ${selectedUsers.length} staff member${selectedUsers.length === 1 ? "" : "s"}.`);
      setSelectedStaffIds([]);
      setBulkRole("");
      setBulkStatus("");
    } catch (error: any) {
      toast.error(error?.message || "Failed to update selected staff.");
    } finally {
      setApplyingBulkAccess(false);
    }
  };

  const [trashedProjects, setTrashedProjects] = useState<any[]>([]);
  const [loadingTrash, setLoadingTrash] = useState(false);
  const [recoverConfirm, setRecoverConfirm] = useState(false);

  const fetchTrash = async (retries = 3) => {
    if (!user) return;
    setLoadingTrash(true);
    try {
      const res = await fetch("/api/projects/trash", {
        headers: { "x-user-uid": user.uid }
      });
      if (res.ok) {
        setTrashedProjects(await res.json());
      }
    } catch (error) {
      if (retries > 0) {
        setTimeout(() => fetchTrash(retries - 1), 2000);
      } else {
        console.error("Failed to fetch trash:", error);
      }
    } finally {
      if (retries === 0 || !loadingTrash) {
        setLoadingTrash(false);
      }
    }
  };

  const handleRecoverFromLocalStorage = async () => {
    setRecoverConfirm(false);
    const recovered: any[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith("project_")) {
        try {
          const data = JSON.parse(localStorage.getItem(key) || "{}");
          if (data.id && (!data.ownerId || data.ownerId === user?.uid)) {
            // Ensure ownerId is set for recovered projects
            if (!data.ownerId) {
              data.ownerId = user?.uid;
            }
            recovered.push(data);
          }
        } catch (e) {
          console.error("Failed to parse project from local storage:", e);
        }
      }
    }
    
    if (recovered.length > 0) {
      // Fetch existing projects to deduplicate
      let existingIds = new Set<string>();
      
      const fetchExisting = async (retries = 3) => {
        try {
          const res = await fetch("/api/projects", {
            headers: { "x-user-uid": user?.uid || "" }
          });
          if (res.ok) {
            const projects = await res.json();
            existingIds = new Set(projects.map((p: any) => p.id));
          }
        } catch (e) {
          if (retries > 0) {
            await new Promise(resolve => setTimeout(resolve, 2000));
            await fetchExisting(retries - 1);
          } else {
            console.error("Failed to fetch existing projects:", e);
          }
        }
      };
      await fetchExisting();

      let added = 0;
      toast.info(`Found ${recovered.length} local projects. Syncing to server...`);
      
      for (const p of recovered) {
        if (!existingIds.has(p.id)) {
          added++;
          // Sync to server
          try {
            const res = await fetch(`/api/projects/${p.id}`, {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
                "x-user-uid": user?.uid || ""
              },
              body: JSON.stringify(p)
            });
            if (!res.ok) {
              console.error(`Failed to sync recovered project ${p.id} to server: ${res.statusText}`);
            }
          } catch (e) {
            console.error(`Failed to sync recovered project ${p.id} to server:`, e);
          }
        }
      }

      if (added > 0) {
        toast.success(`Recovered and synced ${added} projects from local storage. Please refresh the dashboard.`);
      } else {
        toast.info("All local projects are already in the dashboard");
      }
    } else {
      toast.info("No projects found in local storage");
    }
  };

  useEffect(() => {
    if (activeTab === "trash") {
      fetchTrash();
    }
  }, [activeTab, user]);

  const handleRestoreProject = async (id: string) => {
    if (!user) return;
    try {
      const res = await fetch(`/api/projects/${id}/restore`, {
        method: "POST",
        headers: { "x-user-uid": user.uid }
      });
      if (res.ok) {
        toast.success("Project restored successfully");
        fetchTrash();
      } else {
        toast.error("Failed to restore project");
      }
    } catch (error) {
      console.error("Error restoring project:", error);
      toast.error("An error occurred");
    }
  };

  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const handlePermanentDelete = async (id: string) => {
    if (!user) return;
    
    try {
      const res = await fetch(`/api/projects/${id}/permanent`, {
        method: "DELETE",
        headers: { "x-user-uid": user.uid }
      });
      if (res.ok) {
        toast.success("Project permanently deleted");
        fetchTrash();
      } else {
        toast.error("Failed to delete project");
      }
    } catch (error) {
      console.error("Error deleting project:", error);
      toast.error("An error occurred");
    } finally {
      setDeleteConfirmId(null);
    }
  };
  const [checkingDrive, setCheckingDrive] = useState(true);

  useEffect(() => {
    const checkDriveStatus = async (retries = 3) => {
      if (!user) return;
      try {
        const res = await fetch("/api/drive/status", {
          headers: { "x-user-uid": user.uid }
        });
        if (!res.ok) throw new Error("Failed to fetch drive status");
        const { connected } = await res.json();
        setDriveConnected(connected);
        setCheckingDrive(false);
      } catch (error) {
        if (retries > 0) {
          setTimeout(() => checkDriveStatus(retries - 1), 2000);
        } else {
          console.warn("Drive status check failed after retries. The server might be waking up.");
          setCheckingDrive(false);
        }
      }
    };
    checkDriveStatus();
  }, [user, setDriveConnected]);

  useEffect(() => {
    if (externalSettings) {
      setLoading(false);
    }
  }, [externalSettings]);

  const handleAddPrompt = () => {
    setSettings((prev) => ({
      ...prev,
      scriptPrompts: [...(Array.isArray(prev.scriptPrompts) ? prev.scriptPrompts : []), ""],
    }));
  };

  const handleUpdatePrompt = (index: number, value: string) => {
    setSettings((prev) => {
      const newPrompts = [...(Array.isArray(prev.scriptPrompts) ? prev.scriptPrompts : [])];
      newPrompts[index] = value;
      return { ...prev, scriptPrompts: newPrompts };
    });
  };

  const handleRemovePrompt = (index: number) => {
    setSettings((prev) => {
      const newPrompts = [...(Array.isArray(prev.scriptPrompts) ? prev.scriptPrompts : [])];
      newPrompts.splice(index, 1);
      return { ...prev, scriptPrompts: newPrompts };
    });
  };

  const handleBack = () => {
    onBack();
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col pb-12">
        <div className="bg-[#f7f3ee] dark:bg-[#2a2520] px-6 py-4 rounded-lg border border-[#ded4c8] dark:border-[#5b5042] flex items-center justify-between mb-6 shadow-sm shrink-0">
          <div className="flex items-center gap-4">
            <button
              onClick={handleBack}
              className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
              title="Back to Dashboard"
            >
              <SettingsIcon className="w-5 h-5" />
            </button>
            <div className="flex flex-col">
              <h2 className="text-2xl font-brandon font-medium text-slate-900 dark:text-slate-100">Global Settings</h2>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Configure global application preferences, rate cards, and folder structures</p>
            </div>
          </div>
        
        <div className="flex items-center gap-3">
          {isSaving && (
            <span className="text-xs text-slate-400 animate-pulse">Saving changes...</span>
          )}
          {!isSaving && (
            <span className="text-xs text-emerald-500 flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" />
              All changes saved
            </span>
          )}
        </div>
      </div>

      <div className="border-b border-slate-200 mb-4">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          {canManageSettings && (
            <button
              onClick={() => setActiveTab("rates")}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-0 ${
                activeTab === "rates"
                  ? "border-[#ffcc01] text-[#ffcc01]"
                  : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300 dark:text-slate-500 dark:hover:text-slate-300 dark:hover:border-slate-700"
              }`}
            >
              Default Rates
            </button>
          )}
          {canManageSettings && (
            <button
              onClick={() => setActiveTab("prompts")}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-0 ${
                activeTab === "prompts"
                  ? "border-[#ffcc01] text-[#ffcc01]"
                  : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300 dark:text-slate-500 dark:hover:text-slate-300 dark:hover:border-slate-700"
              }`}
            >
              Script Extraction
            </button>
          )}
          {canManageIntegrations && (
            <button
              onClick={() => setActiveTab("integrations")}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-0 ${
                activeTab === "integrations"
                  ? "border-[#ffcc01] text-[#ffcc01]"
                  : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300 dark:text-slate-500 dark:hover:text-slate-300 dark:hover:border-slate-700"
              }`}
            >
              Integrations
            </button>
          )}
          {canManageDrive && (
            <button
              onClick={() => setActiveTab("drive")}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-0 ${
                activeTab === "drive"
                  ? "border-[#ffcc01] text-[#ffcc01]"
                  : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300 dark:text-slate-500 dark:hover:text-slate-300 dark:hover:border-slate-700"
              }`}
            >
              Drive Folders
            </button>
          )}
          {canManageSettings && (
            <button
              onClick={() => setActiveTab("storage")}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-0 ${
                activeTab === "storage"
                  ? "border-[#ffcc01] text-[#ffcc01]"
                  : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300 dark:text-slate-500 dark:hover:text-slate-300 dark:hover:border-slate-700"
              }`}
            >
              Storage and Quota
            </button>
          )}
          {canManageUsers && (
            <button
              onClick={() => setActiveTab("permissions")}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-0 ${
                activeTab === "permissions"
                  ? "border-[#ffcc01] text-[#ffcc01]"
                  : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300 dark:text-slate-500 dark:hover:text-slate-300 dark:hover:border-slate-700"
              }`}
            >
              Permissions
            </button>
          )}
          {canManageUsers && (
            <button
              onClick={() => setActiveTab("domain")}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-0 ${
                activeTab === "domain"
                  ? "border-[#ffcc01] text-[#ffcc01]"
                  : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300 dark:text-slate-500 dark:hover:text-slate-300 dark:hover:border-slate-700"
              }`}
            >
                  User Access
            </button>
          )}
          {canManageTrash && (
            <button
              onClick={() => setActiveTab("trash")}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-0 ${
                activeTab === "trash"
                  ? "border-[#ffcc01] text-[#ffcc01]"
                  : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300 dark:text-slate-500 dark:hover:text-slate-300 dark:hover:border-slate-700"
              }`}
            >
              Deleted Projects
            </button>
          )}
        </nav>
      </div>

      <div className={cn(
        activeTab === "rates" && canManageSettings
          ? "space-y-0"
          : "bg-white rounded-xl shadow-sm border border-slate-200 p-4"
      )}>
        {activeTab === "rates" && canManageSettings && (
            <div className="space-y-8">
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <div className="max-w-sm space-y-3">
                  <div>
                    <h3 className="text-sm font-bold uppercase tracking-wider text-slate-900">Display Preferences</h3>
                    <p className="mt-1 text-[10px] text-slate-500">
                      Control shared display formatting used across the app.
                    </p>
                  </div>
                  <label className="block space-y-1.5">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Date Format</span>
                    <select
                      value={dateDisplayFormat}
                      onChange={(event) => setDateDisplayFormat(event.target.value as DateDisplayFormat)}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="dd-mm-yyyy">dd-mm-yyyy</option>
                      <option value="dd/mm/yyyy">dd/mm/yyyy</option>
                      <option value="yyyy-mm-dd">yyyy-mm-dd</option>
                    </select>
                  </label>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <RateCardForm
                  rates={settings.defaultRates}
                  assetRates={settings.defaultAssetRates}
                  currency="GBP"
                  onChange={(rates) => setSettings({ ...settings, defaultRates: rates })}
                  onAssetRatesChange={(rates) => setSettings({ ...settings, defaultAssetRates: rates })}
                  hideRestoreDefaults={true}
                  showOnly="shots"
                />
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <RateCardForm
                  rates={settings.defaultRates}
                  assetRates={settings.defaultAssetRates}
                  currency="GBP"
                  onChange={(rates) => setSettings({ ...settings, defaultRates: rates })}
                  onAssetRatesChange={(rates) => setSettings({ ...settings, defaultAssetRates: rates })}
                  hideRestoreDefaults={true}
                  showOnly="assets"
                />
              </div>
              <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 xl:col-span-1">
                  <RateCardForm
                    rates={settings.defaultRates}
                    assetRates={settings.defaultAssetRates}
                    currency="GBP"
                    onChange={(rates) => setSettings({ ...settings, defaultRates: rates })}
                    onAssetRatesChange={(rates) => setSettings({ ...settings, defaultAssetRates: rates })}
                    hideRestoreDefaults={true}
                    showOnly="misc"
                  />
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 space-y-4 xl:col-span-3">
                  <div>
                    <h3 className="text-sm font-bold uppercase tracking-wider text-slate-900">Default Assumptions</h3>
                    <p className="mt-1 text-[10px] text-slate-500">
                      These populate new projects and the reset action in Project Details.
                    </p>
                  </div>
                  <MarkdownField
                    value={settings.defaultAssumptions || DEFAULT_SETUP.assumptions}
                    onChange={(nextValue) => setSettings({ ...settings, defaultAssumptions: nextValue })}
                    rows={6}
                    placeholder="Enter the default quote assumptions in Markdown..."
                    className="text-xs"
                  />
                  <p className="text-[10px] text-slate-500">
                    Use Markdown formatting such as <span className="font-medium">`- bullet point`</span>, <span className="font-medium">**bold**</span>, and numbered lists.
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === "prompts" && canManageSettings && (
            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-semibold text-slate-800 mb-2">Script Extraction Prompts</h3>
                <p className="text-sm text-slate-500 mb-4">
                  These prompts will be used by the AI when extracting information from uploaded script documents.
                </p>
              </div>

              <div className="space-y-4">
                {(Array.isArray(settings.scriptPrompts) ? settings.scriptPrompts : []).map((prompt, index) => (
                  <div key={`prompt-${index}`} className="flex gap-3">
                    <div className="flex-1">
                      <textarea
                        value={prompt || ""}
                        onChange={(e) => handleUpdatePrompt(index, e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm min-h-[60px] resize-y"
                        placeholder="Enter extraction prompt..."
                      />
                    </div>
                    <button
                      onClick={() => handleRemovePrompt(index)}
                      className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors h-fit"
                      title="Remove prompt"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>

              <button
                onClick={handleAddPrompt}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                Add Prompt
              </button>
            </div>
          )}
          {activeTab === "drive" && (
            <div className="max-w-3xl space-y-4 xl:w-1/2">
              <div>
                <h3 className="mb-1 text-sm font-semibold text-slate-800">Default Folder Structure</h3>
                <p className="text-xs text-slate-500">
                  Define the default folder structure created for new projects in Google Drive. You can set colors and create nested folders.
                </p>
              </div>

              <div className="max-h-[520px] overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-2">
                {settings.folderStructure?.map((folder, index) => (
                  <FolderItem
                    key={folder.id || `folder-${index}`}
                    folder={folder}
                    level={0}
                    driveColors={driveColors}
                    onUpdate={(updated) => {
                      const newFolders = [...settings.folderStructure!];
                      newFolders[index] = updated;
                      setSettings(prev => ({ ...prev, folderStructure: newFolders }));
                    }}
                    onDelete={() => {
                      const newFolders = settings.folderStructure!.filter((_, i) => i !== index);
                      setSettings(prev => ({ ...prev, folderStructure: newFolders }));
                    }}
                  />
                ))}

                {(!settings.folderStructure || settings.folderStructure.length === 0) && (
                  <div className="text-center py-8 text-slate-400 text-sm">
                    No folders defined. Click below to add your first folder.
                  </div>
                )}
              </div>

              <button
                onClick={() => {
                  const newFolder: FolderDefinition = {
                    id: Math.random().toString(36).substr(2, 9),
                    name: "New Folder",
                    color: driveColors[0]?.value || '#64748b',
                    children: []
                  };
                  setSettings(prev => ({ ...prev, folderStructure: [...(prev.folderStructure || []), newFolder] }));
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                Add Root Folder
              </button>
            </div>
          )}
          {activeTab === "integrations" && canManageIntegrations && (
            <div className="space-y-8">
              <div>
                <h3 className="text-sm font-semibold text-slate-800 mb-2">Third-Party Integrations</h3>
                <p className="text-sm text-slate-500 mb-6">
                  Connect your external tools to enhance your workflow.
                </p>
              </div>

              <div className="p-6 bg-slate-50 rounded-xl border border-slate-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white rounded-xl border border-slate-200 flex items-center justify-center shadow-sm">
                      <img src="https://www.gstatic.com/images/branding/product/1x/drive_48dp.png" alt="Google Drive" className="w-8 h-8" referrerPolicy="no-referrer" />
                    </div>
                    <div>
                      <h4 className="text-base font-semibold text-slate-900">Google Drive</h4>
                      <p className="text-xs text-slate-500">Sync project documents and automate folder creation.</p>
                    </div>
                  </div>
                  
                  {checkingDrive ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
                  ) : driveConnected ? (
                    <div className="flex items-center gap-2">
                      <span className="flex items-center gap-1 text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full border border-emerald-100">
                        <CheckCircle2 className="w-3 h-3" />
                        Connected
                      </span>
                      <button 
                        onClick={() => {
                          setDriveConnected(false);
                        }}
                        className="text-xs text-slate-400 hover:text-red-600 transition-colors"
                      >
                        Disconnect
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={async () => {
                        try {
                          const res = await fetch(`/api/auth/google/url?uid=${user?.uid}`);
                          const data = await res.json();
                          if (!res.ok) {
                            toast.error(data.error || "Failed to get auth URL");
                            return;
                          }
                          const authWindow = window.open(data.url, 'google_auth', 'width=600,height=700');
                          
                          const handleMessage = (event: MessageEvent) => {
                            if (event.origin !== window.location.origin) return;
                            if (event.data?.type === 'OAUTH_AUTH_SUCCESS') {
                              setDriveConnected(true);
                              window.removeEventListener('message', handleMessage);
                            }
                          };
                          window.addEventListener('message', handleMessage);
                        } catch (error) {
                          console.error("Failed to get auth URL", error);
                          toast.error("Failed to connect to Google Drive");
                        }
                      }}
                      className="px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors text-sm font-medium shadow-sm flex items-center gap-2"
                    >
                      Connect Google Drive
                    </button>
                  )}
                </div>

                <div className="mt-4 grid gap-2 sm:grid-cols-3">
                  <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Shared drives</div>
                    <div className="mt-1 text-sm font-semibold text-slate-900">
                      {loadingIntegrationSummary ? "..." : integrationSummary?.drive.sharedDriveCount ?? "Not available"}
                    </div>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Linked projects</div>
                    <div className="mt-1 text-sm font-semibold text-slate-900">
                      {loadingIntegrationSummary ? "..." : integrationSummary?.drive.linkedProjectCount ?? 0}
                    </div>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Tracked documents</div>
                    <div className="mt-1 text-sm font-semibold text-slate-900">
                      {loadingIntegrationSummary ? "..." : integrationSummary?.drive.trackedDocumentCount ?? 0}
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6 bg-slate-50 rounded-xl border border-slate-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white rounded-xl border border-slate-200 flex items-center justify-center shadow-sm">
                      <img src="/fpt-icon.svg" alt="Autodesk Flow Production Tracking" className="w-8 h-8" />
                    </div>
                    <div>
                      <h4 className="text-base font-semibold text-slate-900">Autodesk Flow Production Tracking</h4>
                      <p className="text-xs text-slate-500">
                        Test the live backend connection before project-level linking and sync work.
                      </p>
                      {fptStatus?.message ? (
                        <p className={cn(
                          "mt-1 text-xs",
                          fptStatus.connected ? "text-emerald-600" : "text-rose-600"
                        )}>
                          {fptStatus.message}
                        </p>
                      ) : null}
                    </div>
                  </div>

                  {checkingFpt ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
                  ) : fptStatus?.connected ? (
                    <div className="flex items-center gap-2">
                      <span className="flex items-center gap-1 text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full border border-emerald-100">
                        <CheckCircle2 className="w-3 h-3" />
                        Connected
                      </span>
                      <button
                        onClick={() => void checkFptStatus()}
                        className="text-xs text-slate-500 hover:text-slate-700 transition-colors"
                      >
                        Re-test
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => void checkFptStatus()}
                      className="px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors text-sm font-medium shadow-sm"
                    >
                      {fptStatus ? "Reconnect" : "Check Connection"}
                    </button>
                  )}
                </div>

                <div className="mt-4 grid gap-2 sm:grid-cols-2">
                  <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Linked projects</div>
                    <div className="mt-1 text-sm font-semibold text-slate-900">
                      {loadingIntegrationSummary ? "..." : integrationSummary?.fpt.linkedProjectCount ?? 0}
                    </div>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Linked staff</div>
                    <div className="mt-1 text-sm font-semibold text-slate-900">{staffIntegrationCounts.fpt.linked}</div>
                  </div>
                </div>

                <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                        <Boxes className="h-4 w-4 text-slate-500" />
                        Import Flow Production Tracking projects
                      </div>
                      <p className="text-xs text-slate-500">
                        Preview all Flow Production Tracking projects, create only clearly unlinked local projects, and pull start/end dates into empty local project dates.
                      </p>
                      <p className="text-[11px] text-slate-400">
                        Existing links are preserved. Name matches and ambiguous cases stay in review and are never auto-imported.
                      </p>
                      <p className="text-[11px] text-slate-400">
                        Linked local project statuses can also be brought across from Flow Production Tracking as a one-off inbound sync.
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => void syncFptProjectStatuses()}
                        disabled={syncingFptProjectStatuses || previewingFptProjectImport || importingFptProjects}
                        className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {syncingFptProjectStatuses ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                        Sync linked project statuses
                      </button>
                      <button
                        type="button"
                        onClick={() => void previewFptProjectImport()}
                        disabled={previewingFptProjectImport || importingFptProjects || syncingFptProjectStatuses}
                        className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {previewingFptProjectImport ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                        Preview import
                      </button>
                      <button
                        type="button"
                        onClick={() => void importFptProjects()}
                        disabled={importingFptProjects || previewingFptProjectImport || syncingFptProjectStatuses || !fptProjectImportPreview || selectedFptProjectImportIds.length === 0}
                        className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {importingFptProjects ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                        Import selected safe projects
                      </button>
                    </div>
                  </div>

                  {fptProjectImportPreview ? (
                    <div className="mt-4 space-y-4">
                      <div className="grid gap-2 sm:grid-cols-4">
                        <div className="rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2">
                          <div className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700">Safe to create</div>
                          <div className="mt-1 text-sm font-semibold text-emerald-900">{fptProjectImportPreview.summary.createCount}</div>
                        </div>
                        <div className="rounded-lg border border-amber-100 bg-amber-50 px-3 py-2">
                          <div className="text-[10px] font-semibold uppercase tracking-wide text-amber-700">Need review</div>
                          <div className="mt-1 text-sm font-semibold text-amber-900">{fptProjectImportPreview.summary.reviewCount}</div>
                        </div>
                        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Skipped</div>
                          <div className="mt-1 text-sm font-semibold text-slate-900">{fptProjectImportPreview.summary.skipCount}</div>
                        </div>
                        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Already linked</div>
                          <div className="mt-1 text-sm font-semibold text-slate-900">{fptProjectImportPreview.summary.existingLinkedCount}</div>
                        </div>
                      </div>

                      <div className="rounded-lg border border-slate-200">
                        <div className="flex flex-col gap-3 border-b border-slate-200 px-3 py-3 lg:flex-row lg:items-center lg:justify-between">
                          <div className="space-y-1">
                            <h5 className="text-xs font-semibold uppercase tracking-wide text-slate-800">Project dashboard</h5>
                            <p className="text-[11px] text-slate-500">
                              Review every Flow Production Tracking project, see which ones are already linked, and choose exactly which safe projects to import.
                            </p>
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            {(["all", "create", "review", "linked"] as FptProjectImportFilter[]).map((filter) => {
                              const label =
                                filter === "all"
                                  ? "All"
                                  : filter === "create"
                                    ? "Safe to create"
                                    : filter === "review"
                                      ? "Needs review"
                                      : "Already linked";
                              return (
                                <button
                                  key={filter}
                                  type="button"
                                  onClick={() => setFptProjectImportFilter(filter)}
                                  className={cn(
                                    "rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors",
                                    fptProjectImportFilter === filter
                                      ? "border-slate-900 bg-slate-900 text-white"
                                      : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
                                  )}
                                >
                                  {label}
                                </button>
                              );
                            })}
                            <button
                              type="button"
                              onClick={() => setSelectedFptProjectImportIds(allSafeFptProjectImportIds)}
                              className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600 transition-colors hover:bg-slate-50"
                            >
                              Select all safe
                            </button>
                            <button
                              type="button"
                              onClick={() => setSelectedFptProjectImportIds([])}
                              className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600 transition-colors hover:bg-slate-50"
                            >
                              Clear selection
                            </button>
                          </div>
                        </div>

                        <div className="grid grid-cols-[auto_minmax(220px,1.6fr)_minmax(120px,0.7fr)_minmax(150px,0.9fr)_minmax(150px,0.9fr)_minmax(120px,0.7fr)_minmax(240px,1.6fr)] gap-3 border-b border-slate-200 bg-slate-50 px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                          <div>Pick</div>
                          <div>Flow Production Tracking project</div>
                          <div>Show code</div>
                          <div>Start date</div>
                          <div>End date</div>
                          <div>Status</div>
                          <div>Local app state</div>
                        </div>

                        <div className="max-h-[26rem] overflow-y-auto">
                          {filteredFptProjectImportRows.length > 0 ? (
                            filteredFptProjectImportRows.map((row) => {
                              const isCreate = row.decision === "create";
                              const isReview = row.decision === "review";
                              const isLinked = row.decision === "skip";
                              const isSelected = selectedFptProjectImportIds.includes(row.fptProjectId);
                              return (
                                <div
                                  key={row.fptProjectId}
                                  className={cn(
                                    "grid grid-cols-[auto_minmax(220px,1.6fr)_minmax(120px,0.7fr)_minmax(150px,0.9fr)_minmax(150px,0.9fr)_minmax(120px,0.7fr)_minmax(240px,1.6fr)] gap-3 border-b border-slate-100 px-3 py-3 text-sm",
                                    isSelected ? "bg-emerald-50/40" : "bg-white",
                                  )}
                                >
                                  <div className="flex items-start pt-0.5">
                                    {isCreate ? (
                                      <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={(event) => {
                                          setSelectedFptProjectImportIds((current) =>
                                            event.target.checked
                                              ? [...new Set([...current, row.fptProjectId])]
                                              : current.filter((id) => id !== row.fptProjectId),
                                          );
                                        }}
                                        className="mt-0.5 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                                      />
                                    ) : (
                                      <span className="inline-block h-4 w-4 rounded border border-slate-200 bg-slate-50" />
                                    )}
                                  </div>
                                  <div>
                                    <div className="font-medium text-slate-900">{row.fptProjectName}</div>
                                    <div className="mt-0.5 text-[11px] text-slate-500">FPT ID {row.fptProjectId}</div>
                                  </div>
                                  <div className="text-slate-700">{row.fptShowCode || "—"}</div>
                                  <div className="text-slate-700">{row.startDate || "—"}</div>
                                  <div className="text-slate-700">{row.endDate || "—"}</div>
                                  <div>
                                    <span
                                      className={cn(
                                        "inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
                                        isCreate
                                          ? "bg-emerald-50 text-emerald-700"
                                          : isReview
                                            ? "bg-amber-50 text-amber-700"
                                            : "bg-slate-100 text-slate-600",
                                      )}
                                    >
                                      {isCreate ? "Create" : isReview ? "Review" : "Linked"}
                                    </span>
                                    {row.fptProjectStatus ? (
                                      <div className="mt-1 text-[11px] text-slate-500">{row.fptProjectStatus}</div>
                                    ) : null}
                                  </div>
                                  <div>
                                    {row.matchedLocalProjectName ? (
                                      <>
                                        <div className="font-medium text-slate-700">{row.matchedLocalProjectName}</div>
                                        <div className="mt-0.5 text-[11px] text-slate-500">
                                          {row.note || (row.matchedLocalProjectLinked ? "Already linked locally." : "Possible local match found.")}
                                        </div>
                                      </>
                                    ) : (
                                      <div className="text-[11px] text-slate-500">
                                        {row.note || "No local match found. This project can be created safely."}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })
                          ) : (
                            <div className="px-3 py-8 text-center text-sm text-slate-500">
                              No projects in this filter.
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          )}

          {activeTab === "storage" && canManageSettings && (
            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-semibold text-slate-800 mb-2">Storage and Quota</h3>
                <p className="text-sm text-slate-500 mb-6">
                  Manage your local and cloud storage usage.
                </p>
              </div>

              <div className="p-6 bg-slate-50 rounded-xl border border-slate-200 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white rounded-xl border border-slate-200 flex items-center justify-center shadow-sm">
                      <Database className="w-6 h-6 text-indigo-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">Local Storage</h4>
                      <p className="text-sm text-slate-500">Browser storage used for offline caching.</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-slate-900">
                      {(() => {
                        let total = 0;
                        for (let i = 0; i < localStorage.length; i++) {
                          const key = localStorage.key(i);
                          if (key) {
                            total += localStorage.getItem(key)?.length || 0;
                          }
                        }
                        return (total / 1024 / 1024).toFixed(2) + " MB";
                      })()}
                    </div>
                    <div className="text-xs text-slate-500">of 5 MB limit</div>
                  </div>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2">
                  <div 
                    className="bg-indigo-600 h-2 rounded-full" 
                    style={{ 
                      width: `${Math.min(100, (() => {
                        let total = 0;
                        for (let i = 0; i < localStorage.length; i++) {
                          const key = localStorage.key(i);
                          if (key) {
                            total += localStorage.getItem(key)?.length || 0;
                          }
                        }
                        return (total / (5 * 1024 * 1024)) * 100;
                      })())}%` 
                    }}
                  ></div>
                </div>
                
                <div className="pt-4 mt-4 border-t border-slate-200 flex justify-end">
                  <button
                    onClick={() => setRecoverConfirm(true)}
                    className="px-4 py-2 bg-white border border-slate-300 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    Recover Projects from Local Storage
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === "trash" && canManageTrash && (
            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-semibold text-slate-800 mb-2">Deleted Projects</h3>
                <p className="text-sm text-slate-500 mb-6">
                  View and restore projects you've recently deleted.
                </p>
              </div>

              {loadingTrash ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : trashedProjects.length === 0 ? (
                <div className="text-center py-12 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
                  <Trash2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 font-medium">No deleted projects found</p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {trashedProjects.map((p) => (
                    <div key={p.id} className="flex items-center justify-between p-4 bg-white rounded-xl border border-slate-200 shadow-sm hover:border-slate-300 transition-all group">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                          <Folder className="w-6 h-6 text-slate-400" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-slate-900">{p.projectName || "Untitled Project"}</h4>
                          <p className="text-xs text-slate-500">Deleted on {new Date(p.updatedAt).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => handleRestoreProject(p.id)}
                          className="px-3 py-1.5 text-xs font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                        >
                          Restore
                        </button>
                        <button
                          onClick={() => setDeleteConfirmId(p.id)}
                          className="px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          Delete Permanently
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === "permissions" && canManageUsers && (
            <div className="space-y-8">
              <div>
                <h3 className="text-sm font-semibold text-slate-800 mb-2">Permissions</h3>
                <p className="text-sm text-slate-500">
                  Role defaults shape access across the app. Project Settings access is now reserved for managerial roles.
                </p>
              </div>

              <div className="grid gap-4 xl:grid-cols-3">
                {ACCESS_ROLE_OPTIONS.map((roleOption) => {
                  const rolePermissions = ROLE_DEFAULT_PERMISSIONS[roleOption.value] || [];
                  const canOpenProjectSettings = rolePermissions.includes("project_settings.manage");
                  return (
                    <div key={roleOption.value} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h4 className="text-sm font-semibold text-slate-900">{roleOption.label}</h4>
                          <p className="mt-1 text-xs text-slate-500">{roleOption.description}</p>
                        </div>
                        <span className={cn(
                          "inline-flex rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider border",
                          canOpenProjectSettings
                            ? "border-emerald-100 bg-emerald-50 text-emerald-700"
                            : "border-slate-200 bg-white text-slate-500"
                        )}>
                          {canOpenProjectSettings ? "Project Settings Access" : "No Project Settings Access"}
                        </span>
                      </div>

                      <div className="mt-4 space-y-3">
                        {PERMISSION_GROUPS.map((group) => {
                          const granted = group.permissions.filter((permission) => rolePermissions.includes(permission.key));
                          return (
                            <div key={`${roleOption.value}-${group.id}`} className="rounded-xl border border-slate-200 bg-white p-3">
                              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{group.label}</p>
                              <p className="mt-2 text-xs text-slate-600">
                                {granted.length > 0
                                  ? granted.map((permission) => permission.label).join(", ")
                                  : "No default permissions in this group."}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {activeTab === "domain" && canManageUsers && (
            <div className="space-y-8">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-sm font-semibold text-slate-800 mb-2">User Access</h3>
                  <p className="text-sm text-slate-500">
                    This area manages app/workspace access linked to People &amp; HR records.
                  </p>
                </div>
                <button
                  onClick={handleOpenStaffImport}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-[#ffcc01] bg-[#ffcc01] px-3 py-1.5 text-xs font-medium text-slate-900 transition-colors hover:brightness-95"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Import Users (CSV)
                </button>
              </div>

              <div className="grid gap-4 xl:grid-cols-2">
                <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <div>
                        <IntegrationMark integrationKey="google" label="Google Workspace" size="lg" bare />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-slate-900">Google Workspace Integration</h4>
                        <p className="mt-1 text-xs text-slate-500">
                          Keep staff records enriched with workspace identity, avatar, and status context.
                        </p>
                      </div>
                    </div>
                    {checkingDrive ? (
                      <Loader2 className="h-5 w-5 animate-spin text-indigo-600" />
                    ) : (
                      <IntegrationStatusBadge linked={driveConnected} />
                    )}
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <div className="flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
                      <div className="min-w-0">
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700">Linked staff</p>
                        <p className="text-[11px] text-emerald-700/80">Connected to Google Workspace</p>
                      </div>
                      <p className="text-xl font-semibold text-emerald-900">{staffIntegrationCounts.google.linked}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleFocusStaffFilter("google_unlinked")}
                      className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-left transition-colors hover:bg-amber-100/70"
                    >
                      <div className="min-w-0">
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-700">Unlinked staff</p>
                        <p className="text-[11px] text-amber-700/80">Still to link or sync</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-semibold text-amber-900">{staffIntegrationCounts.google.notLinked}</p>
                        <p className="text-[11px] font-medium text-amber-700">Show rows</p>
                      </div>
                    </button>
                  </div>

                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <button
                      onClick={fetchDomainUsers}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50"
                    >
                      <RefreshCw className={cn("h-3.5 w-3.5", loadingUsers && "animate-spin")} />
                      Refresh User Access
                    </button>
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <div>
                        <IntegrationMark integrationKey="fpt" label="Flow Production Tracking" size="lg" bare />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-slate-900">Flow Production Tracking Integration</h4>
                        <p className="mt-1 text-xs text-slate-500">
                          See who is already linked to Flow Production Tracking and where people still need matching.
                        </p>
                      </div>
                    </div>
                    {checkingFpt ? (
                      <Loader2 className="h-5 w-5 animate-spin text-indigo-600" />
                    ) : (
                      <IntegrationStatusBadge linked={Boolean(fptStatus?.connected)} />
                    )}
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <div className="flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
                      <div className="min-w-0">
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700">Linked staff</p>
                        <p className="text-[11px] text-emerald-700/80">People with a Flow Production Tracking person link</p>
                      </div>
                      <p className="text-xl font-semibold text-emerald-900">{staffIntegrationCounts.fpt.linked}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleFocusStaffFilter("fpt_unlinked")}
                      className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-left transition-colors hover:bg-amber-100/70"
                    >
                      <div className="min-w-0">
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-700">Unlinked staff</p>
                        <p className="text-[11px] text-amber-700/80">Still need Flow Production Tracking matching</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-semibold text-amber-900">{staffIntegrationCounts.fpt.notLinked}</p>
                        <p className="text-[11px] font-medium text-amber-700">Show rows</p>
                      </div>
                    </button>
                  </div>

                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => void checkFptStatus()}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50"
                    >
                      <RefreshCw className={cn("h-3.5 w-3.5", checkingFpt && "animate-spin")} />
                      {fptStatus ? "Re-check Flow Production Tracking" : "Check Flow Production Tracking"}
                    </button>
                    <select
                      value={fptMatchScope}
                      onChange={(event) => setFptMatchScope(event.target.value as "active_only" | "include_inactive" | "include_all")}
                      className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20"
                      aria-label="Flow Production Tracking match scope"
                    >
                      <option value="active_only">Active only</option>
                      <option value="include_inactive">Include inactive</option>
                      <option value="include_all">Include archived / disabled</option>
                    </select>
                    <button
                      onClick={() => void matchFptStaff()}
                      disabled={matchingFptStaff}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-800 transition-colors hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <RefreshCw className={cn("h-3.5 w-3.5", matchingFptStaff && "animate-spin")} />
                      {matchingFptStaff ? "Matching staff..." : "Match Flow Production Tracking staff"}
                    </button>
                    {fptStatus?.message ? (
                      <span className={cn(
                        "text-xs",
                        fptStatus.connected ? "text-emerald-600" : "text-slate-500"
                      )}>
                        {fptStatus.message}
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h4 className="text-sm font-semibold text-slate-900">Google Workspace Access Control</h4>
                    <p className="mt-1 text-sm text-slate-500">
                      Restrict sign-in to approved email domains while we continue fleshing out the workspace integration.
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-slate-700">
                      {settings.domainRestrictionEnabled ? "Enabled" : "Disabled"}
                    </span>
                    <button
                      onClick={() => setSettings({ ...settings, domainRestrictionEnabled: !settings.domainRestrictionEnabled })}
                      className={cn(
                        "relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2",
                        settings.domainRestrictionEnabled ? "bg-indigo-600" : "bg-slate-200"
                      )}
                    >
                      <span
                        className={cn(
                          "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                          settings.domainRestrictionEnabled ? "translate-x-6" : "translate-x-1"
                        )}
                      />
                    </button>
                  </div>
                </div>

                <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                    <Globe className="w-4 h-4 text-indigo-600" />
                    Allowed Domains
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {(settings.allowedDomains || []).map((domain, idx) => (
                      <div key={idx} className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700">
                        {domain}
                        <button
                          onClick={() => setSettings({ ...settings, allowedDomains: (settings.allowedDomains || []).filter((_, i) => i !== idx) })}
                          className="text-slate-400 transition-colors hover:text-red-600"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => {
                        const domain = window.prompt("Enter domain (e.g. vinefx.com):");
                        if (domain) setSettings({ ...settings, allowedDomains: [...(settings.allowedDomains || []), domain] });
                      }}
                      className="flex items-center gap-1 rounded-lg border border-indigo-100 bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-600 transition-colors hover:bg-indigo-100"
                    >
                      <Plus className="w-3 h-3" /> Add Domain
                    </button>
                  </div>
                </div>
              </div>

              <div ref={staffAccessSectionRef} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h4 className="text-sm font-semibold text-slate-900">User Access</h4>
                    <p className="mt-1 text-sm text-slate-500">
                      Access controls live here, while People &amp; HR remains the primary internal directory for staff records.
                    </p>
                  </div>
                </div>

                <div className="mb-4 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleFocusStaffFilter("all")}
                    className={cn(
                      "inline-flex items-center rounded-full border px-3 py-1 text-[11px] font-medium transition-colors",
                      staffFocusFilter === "all"
                        ? "border-slate-900 bg-slate-900 text-white"
                        : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
                    )}
                  >
                    All accounts
                  </button>
                  <button
                    type="button"
                    onClick={() => handleFocusStaffFilter("google_unlinked")}
                    className={cn(
                      "inline-flex items-center rounded-full border px-3 py-1 text-[11px] font-medium transition-colors",
                      staffFocusFilter === "google_unlinked"
                        ? "border-amber-500 bg-amber-100 text-amber-900"
                        : "border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100/80"
                    )}
                  >
                    Google unlinked · {staffIntegrationCounts.google.notLinked}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleFocusStaffFilter("fpt_unlinked")}
                    className={cn(
                      "inline-flex items-center rounded-full border px-3 py-1 text-[11px] font-medium transition-colors",
                      staffFocusFilter === "fpt_unlinked"
                        ? "border-amber-500 bg-amber-100 text-amber-900"
                        : "border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100/80"
                    )}
                  >
                    Flow Production Tracking unlinked · {staffIntegrationCounts.fpt.notLinked}
                  </button>
                  <span className="text-[11px] text-slate-500">
                    Showing {filteredDomainUsers.length} of {domainUsers.length} linked access records
                  </span>
                  <div className="ml-auto flex flex-wrap items-center gap-2">
                    <div ref={staffStatusFilterRef} className="relative">
                      <button
                        type="button"
                        onClick={() => setShowStaffStatusFilterMenu((current) => !current)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50"
                        aria-haspopup="menu"
                        aria-expanded={showStaffStatusFilterMenu}
                      >
                        {staffStatusFilterLabel}
                        <ChevronDown className="h-3.5 w-3.5" />
                      </button>
                      {showStaffStatusFilterMenu ? (
                        <div className="absolute right-0 z-20 mt-2 w-56 rounded-xl border border-slate-200 bg-white p-2 shadow-lg">
                          <div className="px-2 py-1.5">
                            <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Statuses</span>
                          </div>
                          <div className="mt-1 space-y-1">
                            {ACCESS_STATUS_OPTIONS.map((option) => {
                              const checked = staffStatusFilters.includes(option.value);
                              return (
                                <label
                                  key={option.value}
                                  className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-slate-700 transition-colors hover:bg-slate-50"
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleStaffStatusFilter(option.value)}
                                    className="h-4 w-4 rounded border-slate-300 text-[#ffcc01] focus:ring-[#ffcc01]"
                                  />
                                  <span className="flex-1">{option.label}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      ) : null}
                    </div>
                    <select
                      value={staffGrouping}
                      onChange={(event) => setStaffGrouping(event.target.value as "none" | "department" | "role" | "status")}
                      className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20"
                      aria-label="Group staff access table"
                    >
                      <option value="none">No grouping</option>
                      <option value="department">Group by Department</option>
                      <option value="role">Group by Role</option>
                      <option value="status">Group by Status</option>
                    </select>
                    <button
                      onClick={fetchDomainUsers}
                      className="flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50"
                      title="Re-fetch the current internal staff directory and access state."
                    >
                      <RefreshCw className={cn("w-3.5 h-3.5", loadingUsers && "animate-spin")} />
                      Refresh User Access
                    </button>
                  </div>
                </div>

                {selectedStaffIds.length > 0 ? (
                  <div className="mb-6 flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <div className="min-w-[150px]">
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Selected</p>
                      <p className="mt-1 text-xs font-medium text-slate-900">
                        {selectedStaffIds.length} staff member{selectedStaffIds.length === 1 ? "" : "s"}
                      </p>
                    </div>
                    <div className="min-w-[180px]">
                      <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-slate-500">Role</label>
                      <select
                        value={bulkRole}
                        onChange={(event) => setBulkRole(event.target.value as UserRole | "")}
                        className={cn("w-full rounded-lg px-3 py-1.5 text-xs font-medium outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20", bulkStatus ? getStatusToneClasses(bulkStatus) : "border-slate-300 bg-white text-slate-800")}
                      >
                        <option value="">Leave unchanged</option>
                        {ACCESS_ROLE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="min-w-[180px]">
                      <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-slate-500">Status</label>
                      <select
                        value={bulkStatus}
                        onChange={(event) => setBulkStatus(event.target.value)}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-800 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20"
                      >
                        <option value="">Leave unchanged</option>
                        {ACCESS_STATUS_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleApplyBulkAccess}
                        disabled={applyingBulkAccess || (!bulkRole && !bulkStatus)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-[#ffcc01] bg-[#ffcc01] px-3 py-2 text-xs font-medium text-slate-900 transition-colors hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {applyingBulkAccess ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                        Apply to Selected
                      </button>
                      <button
                        onClick={() => {
                          setSelectedStaffIds([]);
                          setBulkRole("");
                          setBulkStatus("");
                        }}
                        className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                ) : null}

                {loadingUsers ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
                  </div>
                ) : filteredDomainUsers.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center">
                    <p className="text-sm font-medium text-slate-700">No user access records match this filter.</p>
                    <p className="mt-1 text-xs text-slate-500">
                      {staffFocusFilter === "google_unlinked"
                        ? "Everyone in the current access list already has a Google Workspace link."
                        : staffFocusFilter === "fpt_unlinked"
                          ? "Everyone in the current access list already has a Flow Production Tracking person link."
                          : "Try another grouping or refresh the access list."}
                    </p>
                  </div>
                ) : (
                  <div className="overflow-hidden rounded-xl border border-slate-200">
                    <table className="w-full text-left text-sm">
                      <thead className="border-b border-slate-200 bg-slate-50 text-slate-600 font-medium">
                        <tr>
                          <th className="px-4 py-3 w-12">
                            <input
                              type="checkbox"
                              checked={filteredDomainUsers.length > 0 && visibleSelectedStaffIds.length === filteredDomainUsers.length}
                              onChange={toggleSelectAllStaff}
                              className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                              aria-label="Select all staff"
                            />
                          </th>
                          <th className="px-4 py-3">Person</th>
                          <th className="px-4 py-3">Email</th>
                          <th className="px-4 py-3">Role</th>
                          <th className="px-4 py-3">Status</th>
                          <th className="px-4 py-3">Integrations</th>
                          <th className="px-4 py-3">Permissions</th>
                          <th className="px-4 py-3 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {groupedStaffRows.map((group) => (
                          <React.Fragment key={group.key}>
                            {staffGrouping !== "none" ? (
                              <tr className="bg-slate-100/70">
                                <td colSpan={8} className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                                  {group.label} · {group.users.length}
                                </td>
                              </tr>
                            ) : null}
                            {group.users.map((u) => {
                              const integrationStatus = u.integrationStatus || buildPersonIntegrationSummary(u);
                              const linkedIntegrations = Object.values(integrationStatus).filter((entry: any) => entry.state === "linked");

                              return (
                                <tr
                                  key={u.id}
                                  onDoubleClick={() => setEditingAccessUser(u)}
                                  className={cn(
                                    "transition-colors hover:bg-slate-50",
                                    selectedStaffIds.includes(u.id) && "bg-indigo-50/40"
                                  )}
                                >
                                  <td className="px-4 py-3" onClick={(event) => event.stopPropagation()}>
                                    <input
                                      type="checkbox"
                                      checked={selectedStaffIds.includes(u.id)}
                                      onChange={() => toggleStaffSelection(u.id)}
                                      className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                      aria-label={`Select ${u.name}`}
                                    />
                                  </td>
                                  <td className="px-4 py-3">
                                    <div className="flex items-center gap-3">
                                      <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden">
                                        {u.photoUrl ? <img src={u.photoUrl} className="w-full h-full object-cover" referrerPolicy="no-referrer" /> : <User className="w-4 h-4 text-slate-400" />}
                                      </div>
                                      <div>
                                        <div className="font-medium text-slate-900">{u.name}</div>
                                        <div className="text-xs text-slate-500">{u.department || u.title || "Staff record"}</div>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-4 py-3 text-slate-500">{u.email}</td>
                                  <td className="px-4 py-3">
                                    <select
                                      value={u.role || "standard"}
                                      onClick={(event) => event.stopPropagation()}
                                      onChange={async (event) => {
                                        const nextRole = event.target.value as UserRole;
                                        await handleUpdateUserAccess(
                                          u.id,
                                          {
                                            role: nextRole,
                                            status: u.status || "active",
                                            customPermissions: u.customPermissions || [],
                                          },
                                          { skipRefresh: true }
                                        );
                                        setDomainUsers((current) =>
                                          current.map((entry) =>
                                            entry.id === u.id ? { ...entry, role: nextRole } : entry
                                          )
                                        );
                                      }}
                                      className="rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-[11px] font-medium text-slate-700 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20"
                                      aria-label={`Role for ${u.name}`}
                                    >
                                      {ACCESS_ROLE_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                          {option.label}
                                        </option>
                                      ))}
                                    </select>
                                  </td>
                                  <td className="px-4 py-3">
                                    <select
                                      value={u.status || "active"}
                                      onClick={(event) => event.stopPropagation()}
                                      onChange={async (event) => {
                                        const nextStatus = event.target.value;
                                        await handleUpdateUserAccess(
                                          u.id,
                                          {
                                            role: (u.role || "standard") as UserRole,
                                            status: nextStatus,
                                            customPermissions: u.customPermissions || [],
                                          },
                                          { skipRefresh: true }
                                        );
                                        setDomainUsers((current) =>
                                          current.map((entry) =>
                                            entry.id === u.id ? { ...entry, status: nextStatus } : entry
                                          )
                                        );
                                      }}
                                      className={cn(
                                        "rounded-lg px-2.5 py-1.5 text-[11px] font-medium outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20",
                                        getStatusToneClasses(u.status || "active")
                                      )}
                                      aria-label={`Status for ${u.name}`}
                                    >
                                      {ACCESS_STATUS_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                          {option.label}
                                        </option>
                                      ))}
                                    </select>
                                  </td>
                                  <td className="px-4 py-3">
                                    <div className="flex flex-wrap gap-1.5">
                                      {(Object.values(integrationStatus) as any[]).map((entry) => (
                                        <IntegrationBadge
                                          key={entry.key}
                                          integrationKey={entry.key}
                                          label={entry.label.replace(" Workspace", "")}
                                          state={entry.state}
                                        />
                                      ))}
                                      {linkedIntegrations.length === 0 ? (
                                        <span className="text-[10px] text-slate-400">No links yet</span>
                                      ) : null}
                                    </div>
                                  </td>
                                  <td className="px-4 py-3 text-slate-500 text-xs">
                                    {u.customPermissions?.length
                                      ? `${u.customPermissions.length} extra permission${u.customPermissions.length === 1 ? "" : "s"}`
                                      : "Role defaults"}
                                  </td>
                                  <td className="px-4 py-3 text-right">
                                    <div className="flex justify-end gap-2">
                                      <button
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          openManualFptLinker(u);
                                        }}
                                        className={cn(
                                          "rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors",
                                          integrationStatus.fpt?.state === "linked"
                                            ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100/70"
                                            : "border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100/80",
                                        )}
                                      >
                                        {integrationStatus.fpt?.state === "linked" ? "Relink FPT" : "Link FPT"}
                                      </button>
                                      <button
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          onOpenPeoplePerson?.(u.id);
                                        }}
                                        className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50"
                                      >
                                        People &amp; HR
                                      </button>
                                      <button
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          setEditingAccessUser(u);
                                        }}
                                        className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50"
                                      >
                                        Access
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showStaffImportModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm">
          <div className="flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Import Internal Staff (CSV)</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Upload a staff CSV, map the incoming columns to our People &amp; HR fields, preview the matches, then import into the internal staff directory only.
                </p>
              </div>
              <button
                onClick={closeStaffImportModal}
                className="rounded-full p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
                aria-label="Close staff import"
              >
                <Trash2 className="h-4 w-4 rotate-45" />
              </button>
            </div>

            <div className="overflow-y-auto px-6 py-5">
              <div className="grid gap-6 xl:grid-cols-[1.2fr_1fr]">
                <div className="space-y-6">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Source File</p>
                        <p className="mt-1 text-sm text-slate-700">
                          {staffImportFileName || "Choose a CSV file to begin."}
                        </p>
                        {staffImportRows.length > 0 ? (
                          <p className="mt-1 text-xs text-slate-500">
                            {staffImportRows.length} row{staffImportRows.length === 1 ? "" : "s"} loaded
                          </p>
                        ) : null}
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          ref={staffImportInputRef}
                          type="file"
                          accept=".csv,text/csv"
                          className="hidden"
                          onChange={handleStaffImportFileChange}
                        />
                        <button
                          onClick={() => staffImportInputRef.current?.click()}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-100"
                        >
                          <Plus className="h-3.5 w-3.5" />
                          {staffImportFileName ? "Replace CSV" : "Choose CSV"}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-white p-5">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <h3 className="text-sm font-semibold text-slate-900">Column Mapping</h3>
                        <p className="mt-1 text-sm text-slate-500">
                          Exact matches are highlighted in green, looser guesses in amber, and you can still adjust anything before previewing.
                        </p>
                      </div>
                    </div>

                    <div className="mt-4 flex flex-wrap gap-2 text-[11px] text-slate-600">
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-emerald-700">
                        <span className="h-2 w-2 rounded-full bg-emerald-500" />
                        Exact match
                      </span>
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-amber-700">
                        <span className="h-2 w-2 rounded-full bg-amber-500" />
                        Loose guess
                      </span>
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-sky-700">
                        <span className="h-2 w-2 rounded-full bg-sky-500" />
                        Saved preset
                      </span>
                    </div>

                    <div className="mt-5 grid gap-4 md:grid-cols-2">
                      {STAFF_IMPORT_FIELD_OPTIONS.map((field) => {
                        const mappingConfidence = staffImportMappingConfidence[field.key];
                        const selectClassName =
                          mappingConfidence === "exact"
                            ? "border-emerald-300 bg-emerald-50/60"
                            : mappingConfidence === "loose"
                              ? "border-amber-300 bg-amber-50/60"
                              : mappingConfidence === "saved"
                                ? "border-sky-300 bg-sky-50/60"
                                : "border-slate-300 bg-white";

                        return (
                        <label key={field.key} className="block">
                          <span className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                            {field.label}
                          </span>
                          <div className="mt-1">
                            <select
                              value={staffImportMapping[field.key] || ""}
                              onChange={(event) => updateStaffImportMapping(field.key, event.target.value)}
                              className={cn(
                                "w-full rounded-xl border px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20",
                                selectClassName,
                              )}
                            >
                              <option value="">Not mapped</option>
                              {staffImportHeaders.map((header) => (
                                <option key={`${field.key}-${header}`} value={header}>
                                  {header}
                                </option>
                              ))}
                            </select>
                          </div>
                          <span className="mt-1 block text-xs text-slate-400">{field.description}</span>
                        </label>
                        );
                      })}
                    </div>

                    {missingRequiredStaffImportMappings.length > 0 ? (
                      <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                        Before previewing, {missingRequiredStaffImportMappings[0]}.
                      </div>
                    ) : null}

                    {staffImportMappingWarnings.length > 0 ? (
                      <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                        {staffImportMappingWarnings[0]}
                      </div>
                    ) : null}
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-white p-5">
                    <label className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={staffImportSetMissingInactive}
                        onChange={(event) => {
                          setStaffImportSetMissingInactive(event.target.checked);
                          setStaffImportPreview(null);
                        }}
                        className="mt-1 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <div>
                        <p className="text-sm font-medium text-slate-900">Mark missing internal staff inactive</p>
                        <p className="mt-1 text-sm text-slate-500">
                          After import, internal people not present in this CSV will be marked inactive in the staff directory. This does not touch external contacts.
                        </p>
                      </div>
                    </label>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="rounded-2xl border border-slate-200 bg-white p-5">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <h3 className="text-sm font-semibold text-slate-900">Preview</h3>
                        <p className="mt-1 text-sm text-slate-500">
                          We’ll show what will be created, updated, skipped, and optionally marked inactive before anything is written.
                        </p>
                      </div>
                      <button
                        onClick={() => runStaffImportPreview(true)}
                        disabled={staffImportLoading || !staffImportRows.length}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {staffImportLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                        Preview Import
                      </button>
                    </div>

                    {staffImportPreview ? (
                      <div className="mt-4 space-y-3">
                        <div className="grid grid-cols-2 gap-2 lg:grid-cols-5">
                          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Create</p>
                            <p className="mt-0.5 text-lg font-semibold text-slate-900">{staffImportPreview.createdCount}</p>
                          </div>
                          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Update</p>
                            <p className="mt-0.5 text-lg font-semibold text-slate-900">{staffImportPreview.updatedCount}</p>
                          </div>
                          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Skip</p>
                            <p className="mt-0.5 text-lg font-semibold text-slate-900">{staffImportPreview.skippedCount}</p>
                          </div>
                          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-700">Review</p>
                            <p className="mt-0.5 text-lg font-semibold text-amber-900">{staffImportPreview.reviewCount}</p>
                          </div>
                          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Inactive</p>
                            <p className="mt-0.5 text-lg font-semibold text-slate-900">{staffImportPreview.markedInactiveCount}</p>
                          </div>
                        </div>

                        {unresolvedStaffImportReviews > 0 ? (
                          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                            <div className="flex items-center justify-between gap-3">
                              <span>{unresolvedStaffImportReviews} row{unresolvedStaffImportReviews === 1 ? "" : "s"} still need a review decision before import.</span>
                              <button
                                type="button"
                                onClick={scrollToFirstUnresolvedStaffImportReview}
                                className="shrink-0 rounded-md border border-amber-300 bg-white px-2 py-1 text-[11px] font-semibold text-amber-800 hover:bg-amber-100"
                              >
                                Show next
                              </button>
                            </div>
                          </div>
                        ) : null}

                        <div className="rounded-lg border border-slate-200">
                          <div className="border-b border-slate-200 bg-slate-50 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                            Row Preview
                            {staffImportPreview.rows.some((row) => row.action === "review") ? (
                              <span className="ml-2 font-medium normal-case tracking-normal text-amber-700">
                                Showing all {visibleStaffImportReviewRows.length} review rows
                              </span>
                            ) : null}
                            {staffImportPreview.rows.some((row) => row.action === "review") ? (
                              <span className="ml-2 font-medium normal-case tracking-normal text-slate-500">
                                Sorted by name
                              </span>
                            ) : null}
                          </div>
                          <div ref={staffImportReviewListRef} className="max-h-[520px] overflow-y-auto">
                            {staffImportPreview.rows.length === 0 ? (
                              <div className="px-4 py-6 text-sm text-slate-500">No actionable rows found yet.</div>
                            ) : (
                              <div className="divide-y divide-slate-100">
                                {visibleStaffImportReviewRows.map((row, index) => {
                                  const needsDecision = row.action === "review" && !staffImportReviewDecisions[String(row.rowNumber)];
                                  const previousRow = index > 0 ? visibleStaffImportReviewRows[index - 1] : null;
                                  const currentName = String(row.name || "Unnamed row").trim();
                                  const previousName = String(previousRow?.name || "").trim();
                                  const showNameGroup = currentName.localeCompare(previousName, undefined, { sensitivity: "base" }) !== 0;
                                  return (
                                  <React.Fragment key={`import-preview-${row.rowNumber}-${row.email}-${row.name}`}>
                                  {showNameGroup ? (
                                    <div className="border-b border-slate-200 bg-slate-50/80 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                                      {currentName || "Unnamed row"}
                                    </div>
                                  ) : null}
                                  <div
                                    data-staff-import-row={row.rowNumber}
                                    className={cn(
                                      "px-3 py-2",
                                      needsDecision && "bg-amber-50/70 ring-1 ring-inset ring-amber-200"
                                    )}
                                  >
                                    <div className="flex items-start gap-2">
                                    <div className="flex min-w-0 flex-1 items-start gap-2">
                                      <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-slate-100 text-[11px] font-semibold text-slate-500">
                                          {row.photoUrl ? (
                                            <img
                                              src={row.photoUrl}
                                              alt={row.name || "Imported staff preview"}
                                              className="h-full w-full object-cover"
                                              referrerPolicy="no-referrer"
                                            />
                                          ) : (
                                            <span>
                                              {(row.name || "?")
                                                .split(/\s+/)
                                                .filter(Boolean)
                                                .slice(0, 2)
                                                .map((part) => part[0])
                                                .join("")
                                                .toUpperCase() || "?"}
                                            </span>
                                          )}
                                        </div>
                                      <div className="min-w-0">
                                          <div className="flex flex-wrap items-center gap-2">
                                            <p className="truncate text-xs font-semibold text-slate-900">{row.name || "Unnamed row"}</p>
                                            <span className="text-[11px] font-medium text-slate-400">
                                              Row {row.rowNumber}
                                            </span>
                                          </div>
                                          <p className="truncate text-[11px] text-slate-500">{row.email || "No email supplied"}</p>
                                          <p className="mt-0.5 text-[11px] text-slate-500">
                                            {row.matchReason === "none" ? "New person" : row.matchReason.replace(/_/g, " ")}
                                          </p>
                                        </div>
                                      </div>
                                      <span className={cn(
                                        "inline-flex shrink-0 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider",
                                        row.action === "create"
                                          ? "bg-indigo-50 text-indigo-700"
                                        : row.action === "update"
                                            ? "bg-emerald-50 text-emerald-700"
                                          : row.action === "review"
                                            ? "bg-amber-50 text-amber-700"
                                            : "bg-slate-100 text-slate-600"
                                      )}>
                                        {needsDecision ? "needs decision" : row.action}
                                      </span>
                                    </div>
                                    {row.warnings.length > 0 ? (
                                      <p className="mt-1 line-clamp-2 text-[11px] text-amber-600">{row.warnings.join(" · ")}</p>
                                    ) : null}
                                    {(row.contractStartDate || row.contractEndDate || row.conflictingContractStartDate || row.conflictingContractEndDate) ? (
                                      <div className="mt-1.5 grid gap-1 sm:grid-cols-2">
                                        {(row.contractStartDate || row.contractEndDate) ? (
                                          <div className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5">
                                            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                                              This row
                                            </p>
                                            <p className="mt-0.5 text-[11px] font-medium text-slate-700">
                                              {formatStaffImportContractRange(row.contractStartDate, row.contractEndDate)}
                                            </p>
                                          </div>
                                        ) : null}
                                        {(row.conflictingContractStartDate || row.conflictingContractEndDate || row.conflictingRowNumber) ? (
                                          <div className="rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5">
                                            <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-700">
                                              {row.conflictingRowNumber ? `CSV row ${row.conflictingRowNumber}` : "Conflicting record"}
                                            </p>
                                            <p className="mt-0.5 text-[11px] font-medium text-amber-800">
                                              {formatStaffImportContractRange(row.conflictingContractStartDate, row.conflictingContractEndDate)}
                                            </p>
                                          </div>
                                        ) : null}
                                      </div>
                                    ) : null}
                                    {row.action === "review" ? (
                                      <div className={cn("mt-1.5 rounded-md border bg-white px-2 py-2", needsDecision ? "border-amber-400 shadow-[0_0_0_1px_rgba(251,191,36,0.25)]" : "border-amber-200")}>
                                          {row.candidateMatches?.length ? (
                                            <div className="space-y-1">
                                              {row.candidateMatches.map((candidate) => (
                                                <div key={`${row.rowNumber}-${candidate.personId}`} className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5">
                                                  <p className="text-xs font-medium text-slate-900">{candidate.name}</p>
                                                  <p className="text-xs text-slate-500">
                                                    {candidate.email || "No email"}{candidate.department ? ` · ${candidate.department}` : ""}{candidate.title ? ` · ${candidate.title}` : ""}
                                                  </p>
                                                  <p className="text-[10px] text-amber-700">
                                                    {candidate.confidenceLabel === "strong" ? "Strong" : "Possible"} match: {candidate.matchSummary}
                                                  </p>
                                                </div>
                                              ))}
                                            </div>
                                          ) : (
                                            <p className="text-[11px] text-slate-500">
                                              Choose how to handle this row, then run Preview Import again to recalculate the import.
                                            </p>
                                          )}
                                          <label className="mt-1.5 block">
                                            <select
                                              value={(() => {
                                                const decision = staffImportReviewDecisions[String(row.rowNumber)];
                                                if (!decision) return "";
                                                if (decision.decision === "create_new") return "create_new";
                                                if (decision.decision === "skip") return "skip";
                                                return `link:${decision.personId}`;
                                              })()}
                                              onChange={(event) => updateStaffImportReviewDecision(row.rowNumber, event.target.value)}
                                              className={cn(
                                                "w-full rounded-md border bg-white px-2 py-1.5 text-xs text-slate-800 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20",
                                                needsDecision ? "border-amber-400 ring-2 ring-amber-200" : "border-slate-300"
                                              )}
                                            >
                                              <option value="">Choose a decision</option>
                                              {(row.candidateMatches || []).map((candidate) => (
                                                <option key={`decision-${row.rowNumber}-${candidate.personId}`} value={`link:${candidate.personId}`}>
                                                  Link to {candidate.name}
                                                </option>
                                              ))}
                                              <option value="create_new">
                                                {row.candidateMatches?.length ? "Create new person" : "Import this row anyway"}
                                              </option>
                                              <option value="skip">Skip this row</option>
                                            </select>
                                          </label>
                                      </div>
                                    ) : null}
                                  </div>
                                  </React.Fragment>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-5 rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-sm text-slate-500">
                        Load a CSV and run a preview to see the proposed creates, updates, and inactive changes.
                      </div>
                    )}
                  </div>

                </div>
              </div>

              {staffImportRows.length > 0 ? (
                <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="text-sm font-semibold text-slate-900">CSV Snapshot</h3>
                    <span className="text-xs text-slate-500">
                      Showing first {Math.min(visibleStaffImportPreviewRows.length, 8)} of {staffImportRows.length} rows
                    </span>
                  </div>
                  <div className="mt-4 overflow-x-auto">
                    <table className="min-w-full text-left text-sm">
                      <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                        <tr>
                          {staffImportHeaders.slice(0, 6).map((header) => (
                            <th key={`header-${header}`} className="px-3 py-2 font-semibold">{header}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {visibleStaffImportPreviewRows.map((row, index) => (
                          <tr key={`csv-row-${index}`}>
                            {staffImportHeaders.slice(0, 6).map((header) => (
                              <td key={`csv-row-${index}-${header}`} className="px-3 py-2 text-slate-600">
                                {row[header] || <span className="text-slate-300">—</span>}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-6 py-4">
              {staffImportDisabledReason ? (
                <p className="mr-auto text-xs text-slate-500">
                  {staffImportDisabledReason}
                </p>
              ) : null}
              <button
                onClick={closeStaffImportModal}
                className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={() => runStaffImportPreview(false)}
                disabled={Boolean(staffImportDisabledReason)}
                title={staffImportDisabledReason || "Import staff"}
                className="inline-flex items-center gap-1.5 rounded-lg border border-[#ffcc01] bg-[#ffcc01] px-4 py-2 text-sm font-medium text-slate-900 transition-colors hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {staffImportApplying ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Import Staff
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirmId && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="flex w-full max-w-md flex-col overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-slate-900">
            <div className="flex items-center justify-between border-b border-slate-100 bg-red-50/50 p-4 dark:border-slate-800 dark:bg-red-950/20">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center text-red-600">
                  <Trash2 className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Delete Project Permanently</h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400">This action cannot be undone</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <p className="text-sm text-slate-600 dark:text-slate-300">
                Are you sure you want to permanently delete this project? All data, including settings, rates, shots, and assets, will be lost forever.
              </p>
            </div>
            <div className="flex justify-end gap-3 border-t border-slate-100 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/60">
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-200 dark:text-slate-300 dark:hover:bg-slate-700"
              >
                Cancel
              </button>
              <button
                onClick={() => handlePermanentDelete(deleteConfirmId)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center shadow-md text-sm"
              >
                Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recover Confirmation Modal */}
      {recoverConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="flex w-full max-w-md flex-col overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-slate-900">
            <div className="flex items-center justify-between border-b border-slate-100 bg-indigo-50/50 p-4 dark:border-slate-800 dark:bg-indigo-950/20">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center text-indigo-600">
                  <Database className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Recover Projects</h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Sync local projects to server</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <p className="text-sm text-slate-600 dark:text-slate-300">
                Are you sure you want to recover projects from local storage? This will scan your browser's local storage and sync any missing projects to the server.
              </p>
            </div>
            <div className="flex justify-end gap-3 border-t border-slate-100 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/60">
              <button
                onClick={() => setRecoverConfirm(false)}
                className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-200 dark:text-slate-300 dark:hover:bg-slate-700"
              >
                Cancel
              </button>
              <button
                onClick={handleRecoverFromLocalStorage}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors flex items-center shadow-md text-sm"
              >
                Recover Projects
              </button>
            </div>
          </div>
        </div>
      )}

      {linkingFptUser && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="w-full max-w-3xl overflow-hidden rounded-3xl bg-white shadow-2xl dark:bg-slate-900">
            <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4 dark:border-slate-800">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Link Flow Production Tracking User</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Search Flow Production Tracking and manually link the correct person to {linkingFptUser.name || linkingFptUser.email}.
                </p>
              </div>
              <button
                onClick={() => {
                  setLinkingFptUser(null);
                  setManualFptCandidates([]);
                  setManualFptSearchQuery("");
                }}
                className="rounded-lg px-3 py-2 text-sm font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
              >
                Close
              </button>
            </div>
            <div className="max-h-[80vh] overflow-y-auto p-6">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden">
                    {linkingFptUser.photoUrl ? (
                      <img src={linkingFptUser.photoUrl} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                    ) : (
                      <User className="w-4 h-4 text-slate-400" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{linkingFptUser.name || "Unnamed user"}</p>
                    <p className="text-xs text-slate-500">{linkingFptUser.email || "No email"}</p>
                    <p className="text-xs text-slate-500">{linkingFptUser.department || linkingFptUser.title || "No department or title"}</p>
                  </div>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <div className="relative min-w-[260px] flex-1">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={manualFptSearchQuery}
                    onChange={(event) => setManualFptSearchQuery(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        void searchFptPeople(manualFptSearchQuery);
                      }
                    }}
                    placeholder="Search FPT by name or email"
                    className="w-full rounded-xl border border-slate-300 bg-white pl-9 pr-3 py-2 text-sm text-slate-900 outline-none transition focus:border-[#ffcc01] focus:ring-2 focus:ring-[#ffcc01]/30"
                  />
                </div>
                <select
                  value={fptMatchScope}
                  onChange={(event) => setFptMatchScope(event.target.value as "active_only" | "include_inactive" | "include_all")}
                  className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-[#ffcc01] focus:ring-2 focus:ring-[#ffcc01]/30"
                >
                  <option value="active_only">Active only</option>
                  <option value="include_inactive">Include inactive</option>
                  <option value="include_all">Include archived / disabled</option>
                </select>
                <button
                  onClick={() => void searchFptPeople(manualFptSearchQuery)}
                  disabled={manualFptSearching}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50"
                >
                  {manualFptSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                  Search
                </button>
              </div>

              <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200">
                <div className="border-b border-slate-100 bg-slate-50 px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  Flow Production Tracking results
                </div>
                <div className="max-h-[420px] overflow-y-auto divide-y divide-slate-100">
                  {manualFptCandidates.length === 0 ? (
                    <div className="px-4 py-10 text-center text-sm text-slate-500">
                      {manualFptSearching ? "Searching Flow Production Tracking..." : "No users loaded yet. Search by name or email to find the right match."}
                    </div>
                  ) : (
                    manualFptCandidates.map((candidate) => (
                      <div key={candidate.id} className="flex items-center justify-between gap-4 px-4 py-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="truncate text-sm font-semibold text-slate-900">{candidate.name || "Unnamed Flow Production Tracking user"}</p>
                            <span
                              className={cn(
                                "rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider",
                                candidate.status === "active"
                                  ? "bg-emerald-50 text-emerald-700"
                                  : "bg-amber-50 text-amber-700",
                              )}
                            >
                              {candidate.status}
                            </span>
                          </div>
                          <p className="truncate text-xs text-slate-500">{candidate.email || "No email available"}</p>
                          <p className="mt-1 text-[11px] text-slate-400">FPT ID {candidate.id}</p>
                        </div>
                        <button
                          onClick={() => void submitManualFptLink(candidate)}
                          disabled={manualFptLinking}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-800 transition-colors hover:bg-emerald-100 disabled:opacity-50"
                        >
                          {manualFptLinking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5" />}
                          Link
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {editingAccessUser && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="w-full max-w-4xl overflow-hidden rounded-3xl bg-white shadow-2xl dark:bg-slate-900">
            <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4 dark:border-slate-800">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Manage User Access</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Update {editingAccessUser.name || editingAccessUser.email}'s role, status, and custom permissions.
                </p>
              </div>
              <button
                onClick={() => setEditingAccessUser(null)}
                className="rounded-lg px-3 py-2 text-sm font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
              >
                Close
              </button>
            </div>
            <div className="max-h-[80vh] overflow-y-auto p-6">
              <UserAccessEditor
                userRecord={editingAccessUser}
                onSaveAccess={async (updates) => {
                  await handleUpdateUserAccess(editingAccessUser.id, updates);
                  setEditingAccessUser(null);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
